import React, { useEffect, useState, useMemo, useRef } from 'react';
import { supabase } from '../lib/api';
import { format, subDays, startOfWeek, startOfMonth } from 'date-fns';

// ── Transaction History with period filter ────────────────
const PERIODS = [
  { label: 'Today',      key: 'today' },
  { label: 'This Week',  key: 'week' },
  { label: 'This Month', key: 'month' },
  { label: 'All Time',   key: 'all' },
];

function TransactionHistory({ transactions }) {
  const [period, setPeriod] = useState('all');

  const filtered = useMemo(() => {
    if (period === 'all') return transactions;
    const now = new Date();
    const cutoff = period === 'today' ? subDays(now, 1)
      : period === 'week' ? startOfWeek(now, { weekStartsOn: 1 })
      : startOfMonth(now);
    return transactions.filter(tx => new Date(tx.created_at) >= cutoff);
  }, [transactions, period]);

  const totals = useMemo(() => ({
    credits: filtered.filter(t => t.points_delta > 0).reduce((s, t) => s + t.points_delta, 0),
    debits:  Math.abs(filtered.filter(t => t.points_delta < 0).reduce((s, t) => s + t.points_delta, 0)),
  }), [filtered]);

  return (
    <>
      <div className="section-title">Transaction History</div>
      {/* Period filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {PERIODS.map(p => (
          <button key={p.key} type="button" onClick={() => setPeriod(p.key)} style={{
            padding: '6px 12px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600,
            border: `1.5px solid ${period === p.key ? 'var(--green)' : 'var(--grey-300)'}`,
            background: period === p.key ? 'var(--green-pale)' : '#fff',
            color: period === p.key ? 'var(--green)' : 'var(--grey-700)', cursor: 'pointer',
          }}>{p.label}</button>
        ))}
      </div>
      {/* Totals */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, background: 'var(--green-pale)', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--green)' }}>+{totals.credits}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--grey-700)' }}>Points In</div>
        </div>
        <div style={{ flex: 1, background: 'var(--grey-100)', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800 }}>-{totals.debits}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--grey-700)' }}>Points Out</div>
        </div>
        <div style={{ flex: 1, background: '#1A1A1A', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#FF6200' }}>{filtered.length}</div>
          <div style={{ fontSize: '0.7rem', color: '#aaa' }}>Transactions</div>
        </div>
      </div>
      {/* List */}
      {filtered.length === 0 && <div style={{ textAlign: 'center', color: 'var(--grey-500)', padding: 16, fontSize: '0.85rem' }}>No transactions in this period</div>}
      {filtered.map(tx => (
        <div key={tx.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--grey-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{tx.description || tx.type}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)' }}>{format(new Date(tx.created_at), 'd MMM yyyy, h:mm aaa')}</div>
          </div>
          <div style={{ fontWeight: 700, color: tx.points_delta > 0 ? 'var(--green)' : 'var(--black)', flexShrink: 0, marginLeft: 12 }}>
            {tx.points_delta > 0 ? '+' : ''}{tx.points_delta}
          </div>
        </div>
      ))}
    </>
  );
}

// ── Member Search ─────────────────────────────────────────
// Plain controlled input, lightly debounced. This was the working version
// before — keep it boring so iOS doesn't get clever and refuse to show
// the soft keyboard.
function MemberSearch({ onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    let cancelled = false;
    const handle = setTimeout(async () => {
      const { data } = await supabase.rpc('admin_search_members', { search_query: query });
      if (!cancelled) setResults(data || []);
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="form-input"
        placeholder="Search member by name or email..."
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      {results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid var(--grey-300)', borderRadius: 8, zIndex: 50, boxShadow: 'var(--shadow-lg)' }}>
          {results.map(m => (
            <button key={m.id} type="button" onClick={() => { onSelect(m); setResults([]); setQuery(''); }}
              style={{ width: '100%', padding: '12px 16px', textAlign: 'left', border: 'none', background: 'none', borderBottom: '1px solid var(--grey-100)', cursor: 'pointer' }}>
              <div style={{ fontWeight: 600 }}>{m.first_name} {m.last_name}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--grey-500)' }}>{m.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Member Detail Panel ───────────────────────────────────
function MemberPanel({ member, onClose }) {
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [credit, setCredit] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [adjustPoints, setAdjustPoints] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustType, setAdjustType] = useState('credit');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [reviewSaving, setReviewSaving] = useState({ google: false, facebook: false });
  const [reviewMsg, setReviewMsg] = useState('');
  const [gymSaving, setGymSaving] = useState(false);

  // Book on behalf of member
  const [treatmentList, setTreatmentList] = useState([]);
  const [bookTreatmentId, setBookTreatmentId] = useState('');
  const [bookDate, setBookDate] = useState('');
  const [bookSlots, setBookSlots] = useState([]);
  const [bookSelectedSlotId, setBookSelectedSlotId] = useState('');
  const [bookPaymentMethod, setBookPaymentMethod] = useState('points');
  const [bookLoadingSlots, setBookLoadingSlots] = useState(false);
  const [bookSaving, setBookSaving] = useState(false);
  const [bookMsg, setBookMsg] = useState('');

  // Test push notification
  const [testPushSending, setTestPushSending] = useState(false);
  const [testPushMsg, setTestPushMsg] = useState('');

  const bookTreatment = useMemo(
    () => treatmentList.find(t => t.id === bookTreatmentId) || null,
    [treatmentList, bookTreatmentId]
  );

  // For two-slot treatments (the 60-min NMT assessment on a 30-min diary), only
  // offer reception start times where the consecutive slots are all free.
  const displayBookSlots = useMemo(() => {
    if (!bookSlots.length || !bookTreatment?.duration_mins) return bookSlots;
    const first = bookSlots[0];
    if (!first?.ends_at) return bookSlots;
    const slotMins = (new Date(first.ends_at) - new Date(first.starts_at)) / 60000;
    if (!slotMins || bookTreatment.duration_mins <= slotMins) return bookSlots;
    const needed = Math.ceil(bookTreatment.duration_mins / slotMins);
    const starts = new Set(bookSlots.map(s => new Date(s.starts_at).getTime()));
    const step = slotMins * 60000;
    return bookSlots.filter(s => {
      const t0 = new Date(s.starts_at).getTime();
      for (let i = 1; i < needed; i++) { if (!starts.has(t0 + i * step)) return false; }
      return true;
    });
  }, [bookSlots, bookTreatment]);

  // Has this member already received each review bonus?
  const alreadyReviewed = useMemo(() => ({
    google:   transactions.some(t => t.type === 'google_review_bonus'),
    facebook: transactions.some(t => t.type === 'facebook_review_bonus'),
  }), [transactions]);

  async function handleCreditReview(platform) {
    if (alreadyReviewed[platform]) return;
    const label = platform === 'google' ? 'Google' : 'Facebook';
    if (!window.confirm(`Confirm you have seen this member's 5-star ${label} review? They'll be credited 10 points.`)) return;
    setReviewSaving(s => ({ ...s, [platform]: true })); setReviewMsg('');
    const rpcName = platform === 'google' ? 'admin_credit_google_review' : 'admin_credit_facebook_review';
    const { data, error } = await supabase.rpc(rpcName, { member_uuid: member.id });
    if (!error && data?.ok) {
      setReviewMsg(`✅ 10 points credited for ${label} review`);
      loadMemberData();
    } else if (data?.reason === 'already_credited') {
      setReviewMsg(`⚠️ This member has already received the ${label} review bonus`);
      loadMemberData();
    } else {
      setReviewMsg(`❌ Error crediting ${label} review bonus`);
    }
    setReviewSaving(s => ({ ...s, [platform]: false }));
  }

  useEffect(() => {
    loadMemberData();
  }, [member.id]);

  async function loadMemberData() {
    const { data } = await supabase.rpc('admin_get_member', { member_uuid: member.id });
    if (data) {
      setProfile(data.profile || null);
      setWallet(data.wallet);
      setTransactions(data.transactions || []);
      setBookings(data.bookings || []);
    }
    // £ account credit (separate from points)
    const { data: cp } = await supabase.from('profiles').select('credit_balance_pence').eq('id', member.id).single();
    setCredit(cp?.credit_balance_pence || 0);
  }

  async function handleSendTestPush() {
    setTestPushSending(true); setTestPushMsg('');
    const apiUrl = process.env.REACT_APP_API_URL || 'https://tb-app-production.up.railway.app/api';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${apiUrl}/admin/members/${member.id}/test-push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({}),
      });
      const result = await res.json();
      if (!res.ok || !result.ok) {
        setTestPushMsg(`❌ ${result.error || 'Failed to send'}`);
      } else if (result.sent === 0 && result.removed === 0) {
        setTestPushMsg('⚠️ Member has no notifications enabled — ask them to turn on Notifications in Profile.');
      } else if (result.sent > 0) {
        setTestPushMsg(`✅ Sent to ${result.sent} device${result.sent !== 1 ? 's' : ''}${result.removed > 0 ? ` (${result.removed} stale subscriptions cleaned up)` : ''}`);
      } else {
        setTestPushMsg(`⚠️ ${result.failed || 0} attempts failed, ${result.removed || 0} stale subscriptions removed.`);
      }
    } catch (err) {
      setTestPushMsg(`❌ ${err.message}`);
    }
    setTestPushSending(false);
  }

  async function toggleGymMember() {
    const next = !profile?.is_gym_member;
    setGymSaving(true);
    const { data, error } = await supabase.rpc('admin_set_gym_member', { member_uuid: member.id, is_member: next });
    if (!error && data?.ok) {
      setProfile(p => ({ ...p, is_gym_member: next }));
    }
    setGymSaving(false);
  }

  // Load treatments once when panel mounts (via RPC to bypass RLS)
  useEffect(() => {
    supabase.rpc('list_treatments_for_booking').then(({ data, error }) => {
      if (error) { console.error('Treatment list error:', error); return; }
      setTreatmentList(data || []);
    });
  }, []);

  // Auto-fetch slots when treatment + date change (via the existing admin RPC)
  useEffect(() => {
    if (!bookTreatment || !bookDate) { setBookSlots([]); return; }
    setBookLoadingSlots(true);
    setBookSelectedSlotId('');
    supabase.rpc('admin_list_slots_for_day', {
      p_type: bookTreatment.resource_type,
      p_date: bookDate,
    }).then(({ data, error }) => {
      if (error) console.error('Slot fetch error:', error);
      // Only show slots that are bookable: not blocked, not biohack-reserved,
      // not at capacity, and start in the future.
      const open = (data || []).filter(s =>
        !s.is_blocked &&
        !s.biohack_reserved &&
        s.booked_count < s.capacity &&
        new Date(s.starts_at) > new Date()
      );
      setBookSlots(open);
      setBookLoadingSlots(false);
    });
  }, [bookTreatment, bookDate]);

  async function handleBookSession() {
    if (!bookSelectedSlotId || !bookTreatmentId) return;
    const price = bookTreatment?.payg_price_pence || 0;
    if (bookPaymentMethod === 'points' && (wallet?.balance || 0) < (bookTreatment?.points_cost || 0)) {
      setBookMsg(`❌ Member only has ${wallet?.balance || 0} pts — needs ${bookTreatment.points_cost}`);
      return;
    }
    if (bookPaymentMethod === 'credit' && credit < price) {
      setBookMsg(`❌ Member only has £${(credit / 100).toFixed(2)} credit — needs £${(price / 100).toFixed(2)}`);
      return;
    }

    // Capture the chosen slot's time before we clear state (for the message).
    const chosenSlot = bookSlots.find(s => s.id === bookSelectedSlotId);
    const chosenTime = chosenSlot ? new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short',
      hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(new Date(chosenSlot.starts_at)) : '';
    const treatmentName = bookTreatment?.name || 'session';

    setBookSaving(true); setBookMsg('');
    const apiUrl = process.env.REACT_APP_API_URL || 'https://tb-app-production.up.railway.app/api';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${apiUrl}/bookings/admin-book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          memberId: member.id,
          slotId: bookSelectedSlotId,
          treatmentId: bookTreatmentId,
          paymentMethod: bookPaymentMethod,
        }),
      });
      const data = await res.json();
      setBookSaving(false);
      if (!res.ok || !data.ok) { setBookMsg(`❌ ${data.error || 'Booking failed'}`); return; }

      const payNote =
        bookPaymentMethod === 'points' ? ` · ${bookTreatment.points_cost} pts deducted` :
        bookPaymentMethod === 'credit' ? ` · £${(price / 100).toFixed(2)} from account credit` :
        bookPaymentMethod === 'card_at_reception' ? ' · take payment by card now' :
        ' · take payment in cash now';
      setBookMsg(`✅ Booked ${treatmentName}${chosenTime ? ` for ${chosenTime}` : ''}${payNote}`);
      setBookSelectedSlotId('');
      setBookSlots([]);
      setBookDate('');
      setBookTreatmentId('');
      setBookPaymentMethod('points');
      loadMemberData();
    } catch (err) {
      setBookSaving(false);
      setBookMsg(`❌ ${err.message || 'Booking failed'}`);
    }
  }

  async function handleAdjust() {
    const pts = parseInt(adjustPoints);
    if (!pts || !adjustReason) return;
    setSaving(true); setMsg('');

    const { data, error } = await supabase.rpc('admin_adjust_points', {
      member_uuid: member.id,
      points_amount: pts,
      adjust_type: adjustType,
      reason: adjustReason,
    });

    if (!error && data?.ok) {
      setMsg(`✅ ${pts} points ${adjustType === 'credit' ? 'added' : 'removed'} successfully`);
      setAdjustPoints(''); setAdjustReason('');
      loadMemberData();
    } else {
      setMsg('❌ Error updating points');
    }
    setSaving(false);
  }

  return (
    <div className="card" style={{ padding: 20, marginTop: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h3 style={{ fontWeight: 800, fontSize: '1.1rem' }}>{member.first_name} {member.last_name}</h3>
          <div style={{ color: 'var(--grey-500)', fontSize: '0.85rem' }}>{member.email}</div>
        </div>
        <button type="button" onClick={onClose} style={{ fontSize: '1.2rem', color: 'var(--grey-500)', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
      </div>

      {/* Member tags */}
      <div style={{ background: 'var(--grey-100)', borderRadius: 10, padding: '10px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--grey-700)' }}>Gym Member (TeamUp)</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)' }}>Enables birthday bonus + future gym-member perks</div>
        </div>
        <button
          type="button"
          onClick={toggleGymMember}
          disabled={gymSaving}
          style={{
            width: 50, height: 28, borderRadius: 999, border: 'none', cursor: 'pointer',
            background: profile?.is_gym_member ? '#FF6200' : 'var(--grey-300)',
            position: 'relative', flexShrink: 0, transition: 'background 0.2s',
          }}
        >
          <div style={{
            position: 'absolute', top: 3, left: profile?.is_gym_member ? 25 : 3,
            width: 22, height: 22, borderRadius: '50%', background: '#fff',
            transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </button>
      </div>

      {/* Balance */}
      <div style={{ background: '#1A1A1A', borderRadius: 12, padding: 16, marginBottom: 16, textAlign: 'center', color: '#fff' }}>
        <div style={{ fontSize: '0.75rem', color: '#FF6200', fontWeight: 700, letterSpacing: '0.08em', marginBottom: 4 }}>POINTS BALANCE</div>
        <div style={{ fontSize: '2.5rem', fontWeight: 900 }}>{wallet?.balance || 0}</div>
        <div style={{ color: '#aaa', fontSize: '0.85rem' }}>wellness points</div>
        {credit > 0 && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #333', fontSize: '0.9rem' }}>
            <span style={{ color: '#FF6200', fontWeight: 700 }}>£{(credit / 100).toFixed(2)}</span>
            <span style={{ color: '#aaa' }}> account credit</span>
          </div>
        )}
      </div>

      {/* Adjust Points */}
      <div className="section-title">Adjust Points</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {['credit', 'debit'].map(t => (
          <button key={t} type="button" onClick={() => setAdjustType(t)} style={{
            flex: 1, padding: '10px', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem',
            border: `2px solid ${adjustType === t ? 'var(--green)' : 'var(--grey-300)'}`,
            background: adjustType === t ? 'var(--green-pale)' : '#fff',
            color: adjustType === t ? 'var(--green)' : 'var(--grey-700)', cursor: 'pointer',
          }}>{t === 'credit' ? '+ Add Points' : '– Remove Points'}</button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8, marginBottom: 8 }}>
        <input className="form-input" type="number" placeholder="Points" value={adjustPoints} onChange={e => setAdjustPoints(e.target.value)} min="1" />
        <input className="form-input" placeholder="Reason (e.g. Website pack purchase)" value={adjustReason} onChange={e => setAdjustReason(e.target.value)} />
      </div>
      {msg && <div className={`alert ${msg.startsWith('✅') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 8 }}>{msg}</div>}
      <button className="btn btn-primary btn-full" onClick={handleAdjust} disabled={saving || !adjustPoints || !adjustReason}>
        {saving ? 'Saving…' : `${adjustType === 'credit' ? 'Add' : 'Remove'} ${adjustPoints || '?'} Points`}
      </button>

      {/* Book a Session */}
      <div className="section-title" style={{ marginTop: 20 }}>Book a Session at Reception</div>
      <div style={{ background: 'var(--grey-100)', borderRadius: 10, padding: 14, marginBottom: 8 }}>
        <div style={{ fontSize: '0.78rem', color: 'var(--grey-700)', marginBottom: 10, lineHeight: 1.4 }}>
          Book a treatment on this member's behalf. Pick treatment, date, slot, and how they're paying.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <select className="form-input" value={bookTreatmentId} onChange={e => {
            setBookTreatmentId(e.target.value); setBookSlots([]); setBookSelectedSlotId('');
          }}>
            <option value="">— Choose treatment —</option>
            {treatmentList.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <input className="form-input" type="date" value={bookDate}
            onChange={e => { setBookDate(e.target.value); setBookSelectedSlotId(''); }}
            min={new Date().toISOString().split('T')[0]}
            disabled={!bookTreatmentId} />
        </div>

        {bookLoadingSlots && (
          <div style={{ textAlign: 'center', padding: 12, fontSize: '0.85rem', color: 'var(--grey-500)' }}>Loading slots…</div>
        )}

        {!bookLoadingSlots && bookTreatment && bookDate && displayBookSlots.length === 0 && (
          <div style={{ textAlign: 'center', padding: 12, fontSize: '0.85rem', color: 'var(--grey-500)' }}>
            No available slots for {bookTreatment.name} on this date
          </div>
        )}

        {displayBookSlots.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 6, marginBottom: 12 }}>
            {displayBookSlots.map(slot => {
              const time = new Intl.DateTimeFormat('en-GB', {
                timeZone: 'Europe/London', hour: 'numeric', minute: '2-digit', hour12: false,
              }).format(new Date(slot.starts_at));
              const isSelected = slot.id === bookSelectedSlotId;
              return (
                <button key={slot.id} type="button" onClick={() => setBookSelectedSlotId(slot.id)} style={{
                  padding: '8px 4px', borderRadius: 6, fontSize: '0.85rem', fontWeight: 700,
                  border: `1.5px solid ${isSelected ? '#FF6200' : 'var(--grey-300)'}`,
                  background: isSelected ? '#FFF3E0' : '#fff',
                  color: isSelected ? '#E65100' : 'var(--grey-700)', cursor: 'pointer',
                }}>{time}</button>
              );
            })}
          </div>
        )}

        {bookSelectedSlotId && bookTreatment && (
          <>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--grey-700)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Payment method</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(85px, 1fr))', gap: 6, marginBottom: 10 }}>
              {[
                { key: 'points',             label: bookTreatment.points_cost ? `${bookTreatment.points_cost} pts` : 'No points', disabled: !bookTreatment.points_cost },
                { key: 'card_at_reception',  label: bookTreatment.payg_price_pence ? `Card £${(bookTreatment.payg_price_pence / 100).toFixed(0)}` : 'No PAYG', disabled: !bookTreatment.payg_price_pence },
                { key: 'cash_at_reception',  label: bookTreatment.payg_price_pence ? `Cash £${(bookTreatment.payg_price_pence / 100).toFixed(0)}` : 'No PAYG', disabled: !bookTreatment.payg_price_pence },
                ...(credit > 0 ? [{ key: 'credit', label: bookTreatment.payg_price_pence ? `Credit £${(bookTreatment.payg_price_pence / 100).toFixed(0)}` : 'No PAYG', disabled: !bookTreatment.payg_price_pence || credit < bookTreatment.payg_price_pence }] : []),
              ].map(opt => (
                <button key={opt.key} type="button"
                  onClick={() => !opt.disabled && setBookPaymentMethod(opt.key)}
                  disabled={opt.disabled}
                  style={{
                    padding: '8px 4px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700,
                    border: `2px solid ${bookPaymentMethod === opt.key && !opt.disabled ? '#FF6200' : 'var(--grey-300)'}`,
                    background: bookPaymentMethod === opt.key && !opt.disabled ? '#FFF3E0' : '#fff',
                    color: opt.disabled ? 'var(--grey-300)' : bookPaymentMethod === opt.key ? '#E65100' : 'var(--grey-700)',
                    cursor: opt.disabled ? 'not-allowed' : 'pointer',
                  }}>{opt.label}</button>
              ))}
            </div>
            {bookPaymentMethod === 'points' && bookTreatment.points_cost > 0 && (
              <div style={{ fontSize: '0.78rem', color: 'var(--grey-700)', marginBottom: 8 }}>
                Member balance: <strong>{wallet?.balance || 0} pts</strong>
                {(wallet?.balance || 0) < bookTreatment.points_cost && (
                  <span style={{ color: 'var(--red, #d33)', marginLeft: 6 }}>(insufficient — top up first)</span>
                )}
              </div>
            )}
            {bookPaymentMethod === 'credit' && (
              <div style={{ fontSize: '0.78rem', color: 'var(--grey-700)', marginBottom: 8 }}>
                Account credit: <strong>£{(credit / 100).toFixed(2)}</strong>
                {credit < (bookTreatment.payg_price_pence || 0) && (
                  <span style={{ color: 'var(--red, #d33)', marginLeft: 6 }}>(not enough for this booking)</span>
                )}
              </div>
            )}
          </>
        )}

        {bookMsg && <div className={`alert ${bookMsg.startsWith('✅') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 8 }}>{bookMsg}</div>}

        <button
          type="button"
          className="btn btn-primary btn-full"
          onClick={handleBookSession}
          disabled={
            bookSaving || !bookSelectedSlotId ||
            (bookPaymentMethod === 'points' && (wallet?.balance || 0) < (bookTreatment?.points_cost || 0)) ||
            (bookPaymentMethod === 'credit' && credit < (bookTreatment?.payg_price_pence || 0))
          }
        >
          {bookSaving ? 'Booking…' :
            bookSelectedSlotId ? `Book Session — ${bookPaymentMethod === 'points' ? 'Points' : bookPaymentMethod === 'credit' ? 'Account Credit' : bookPaymentMethod === 'card_at_reception' ? 'Card at Reception' : 'Cash at Reception'}` :
            'Select a slot first'}
        </button>
      </div>

      {/* Review Rewards */}
      <div className="section-title" style={{ marginTop: 20 }}>Review Rewards</div>
      <div style={{ background: 'var(--grey-100)', borderRadius: 10, padding: 14, marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ fontSize: '1.2rem' }}>⭐</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--grey-700)', lineHeight: 1.4 }}>
            Credit this member 10 points for each 5-star review. One-time per platform.
          </div>
        </div>
        {reviewMsg && <div className={`alert ${reviewMsg.startsWith('✅') ? 'alert-success' : reviewMsg.startsWith('⚠️') ? 'alert-warning' : 'alert-error'}`} style={{ marginBottom: 8 }}>{reviewMsg}</div>}
        <button
          type="button"
          className="btn btn-full"
          onClick={() => handleCreditReview('google')}
          disabled={reviewSaving.google || alreadyReviewed.google}
          style={{
            background: alreadyReviewed.google ? 'var(--grey-300)' : '#FF6200',
            color: alreadyReviewed.google ? 'var(--grey-700)' : '#fff',
            cursor: alreadyReviewed.google ? 'not-allowed' : 'pointer',
            marginBottom: 8,
          }}
        >
          {alreadyReviewed.google ? '✓ Google review credited' : reviewSaving.google ? 'Crediting…' : 'Credit 10 Points for Google Review'}
        </button>
        <button
          type="button"
          className="btn btn-full"
          onClick={() => handleCreditReview('facebook')}
          disabled={reviewSaving.facebook || alreadyReviewed.facebook}
          style={{
            background: alreadyReviewed.facebook ? 'var(--grey-300)' : '#1877F2',
            color: alreadyReviewed.facebook ? 'var(--grey-700)' : '#fff',
            cursor: alreadyReviewed.facebook ? 'not-allowed' : 'pointer',
          }}
        >
          {alreadyReviewed.facebook ? '✓ Facebook review credited' : reviewSaving.facebook ? 'Crediting…' : 'Credit 10 Points for Facebook Review'}
        </button>
      </div>

      {/* Send Test Push — verify member's notifications are working */}
      <div className="section-title" style={{ marginTop: 20 }}>Push Notifications</div>
      <div style={{ background: 'var(--grey-100)', borderRadius: 10, padding: 14, marginBottom: 8 }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--grey-700)', lineHeight: 1.4, marginBottom: 10 }}>
          Send a test notification to all this member's enabled devices. Useful for verifying their setup or troubleshooting.
        </div>
        {testPushMsg && (
          <div className={`alert ${testPushMsg.startsWith('✅') ? 'alert-success' : testPushMsg.startsWith('⚠️') ? 'alert-warning' : 'alert-error'}`} style={{ marginBottom: 8 }}>
            {testPushMsg}
          </div>
        )}
        <button
          type="button"
          className="btn btn-full"
          onClick={handleSendTestPush}
          disabled={testPushSending}
          style={{
            background: '#1A1A1A',
            color: '#fff',
            border: 'none',
          }}
        >
          {testPushSending ? 'Sending…' : '🔔 Send Test Push'}
        </button>
      </div>

      {/* Upcoming Bookings */}
      {bookings.length > 0 && (
        <>
          <div className="section-title">Upcoming Bookings</div>
          {bookings.slice(0, 5).map(b => (
            <div key={b.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--grey-100)', display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{b.treatments?.name}</div>
              <div style={{ color: 'var(--grey-500)', fontSize: '0.8rem' }}>
                {b.availability_slots?.starts_at ? format(new Date(b.availability_slots.starts_at), 'd MMM, h:mm aaa') : '—'}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Transaction History with period filter */}
      <TransactionHistory transactions={transactions} />
    </div>
  );
}

// ── Today's / Upcoming Bookings ────────────────────────────
function TodaysBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today'); // 'today' | 'week'

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      const now = new Date();
      const start = new Date(now); start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + (period === 'today' ? 1 : 7));

      // Server-side join + filter via RPC — avoids any embed-filter
      // quirks AND any URL length limits from large IN-lists.
      const { data, error } = await supabase.rpc('admin_get_upcoming_bookings', {
        start_ts: start.toISOString(),
        end_ts:   end.toISOString(),
      });
      if (error) console.error('Bookings RPC error:', error);

      if (!cancelled) {
        setBookings(data || []);
        setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [period]);

  // Group by date for the week view
  const grouped = useMemo(() => {
    const groups = {};
    for (const b of bookings) {
      const day = b.availability_slots.starts_at.slice(0, 10); // YYYY-MM-DD
      if (!groups[day]) groups[day] = [];
      groups[day].push(b);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [bookings]);

  function dayLabel(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    if (d.getTime() === today.getTime()) return 'Today';
    if (d.getTime() === tomorrow.getTime()) return 'Tomorrow';
    return format(d, 'EEEE d MMM');
  }

  const renderCard = (b) => (
    <div key={b.id} className="card" style={{ padding: 14, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ background: 'var(--green-pale)', borderRadius: 8, padding: '8px 10px', textAlign: 'center', minWidth: 52, flexShrink: 0 }}>
        <div style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--green)', lineHeight: 1 }}>
          {format(new Date(b.availability_slots.starts_at), 'h:mm')}
        </div>
        <div style={{ fontSize: '0.6rem', color: 'var(--green)', textTransform: 'uppercase', fontWeight: 600 }}>
          {format(new Date(b.availability_slots.starts_at), 'aaa')}
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{b.treatments?.name}</div>
        <div style={{ color: 'var(--grey-500)', fontSize: '0.8rem' }}>{b.profiles?.first_name} {b.profiles?.last_name}</div>
      </div>
      <div style={{ fontSize: '0.7rem', fontWeight: 600, padding: '3px 8px', borderRadius: 999, background: 'var(--green-pale)', color: 'var(--green)' }}>
        {b.payment_method === 'points' ? `${b.points_cost} pts` : `£${(b.payg_price_pence/100).toFixed(0)}`}
      </div>
    </div>
  );

  return (
    <div>
      <div className="section-title">Bookings</div>
      {/* Period toggle */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[
          { key: 'today', label: 'Today' },
          { key: 'week',  label: 'Next 7 Days' },
        ].map(p => (
          <button key={p.key} type="button" onClick={() => setPeriod(p.key)} style={{
            padding: '6px 14px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600,
            border: `1.5px solid ${period === p.key ? 'var(--green)' : 'var(--grey-300)'}`,
            background: period === p.key ? 'var(--green-pale)' : '#fff',
            color: period === p.key ? 'var(--green)' : 'var(--grey-700)', cursor: 'pointer',
          }}>{p.label}</button>
        ))}
        <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: '0.78rem', color: 'var(--grey-500)' }}>
          {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
        </div>
      </div>

      {loading && <div className="loading-center"><div className="spinner" /></div>}
      {!loading && bookings.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--grey-500)', padding: 24 }}>
          {period === 'today' ? 'No bookings today' : 'No bookings in the next 7 days'}
        </div>
      )}

      {!loading && period === 'today' && bookings.map(renderCard)}

      {!loading && period === 'week' && grouped.map(([day, dayBookings]) => (
        <div key={day} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--grey-700)', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '8px 0 6px' }}>
            {dayLabel(day)} <span style={{ color: 'var(--grey-500)', fontWeight: 500 }}>· {dayBookings.length}</span>
          </div>
          {dayBookings.map(renderCard)}
        </div>
      ))}
    </div>
  );
}

// ── Global Transactions ───────────────────────────────────
function GlobalTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [period, setPeriod] = useState('today');
  const [loading, setLoading] = useState(false);

  const PERIOD_DAYS = { today: 1, week: 7, month: 30, all: null };

  useEffect(() => {
    setLoading(true);
    supabase.rpc('admin_get_all_transactions', { period_days: PERIOD_DAYS[period] })
      .then(({ data }) => setTransactions(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  const totals = useMemo(() => ({
    credits: transactions.filter(t => t.points_delta > 0).reduce((s, t) => s + t.points_delta, 0),
    debits:  Math.abs(transactions.filter(t => t.points_delta < 0).reduce((s, t) => s + t.points_delta, 0)),
  }), [transactions]);

  return (
    <div>
      {/* Period filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {PERIODS.map(p => (
          <button key={p.key} type="button" onClick={() => setPeriod(p.key)} style={{
            padding: '6px 12px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600,
            border: `1.5px solid ${period === p.key ? 'var(--green)' : 'var(--grey-300)'}`,
            background: period === p.key ? 'var(--green-pale)' : '#fff',
            color: period === p.key ? 'var(--green)' : 'var(--grey-700)', cursor: 'pointer',
          }}>{p.label}</button>
        ))}
      </div>

      {/* Totals */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <div style={{ flex: 1, background: 'var(--green-pale)', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--green)' }}>+{totals.credits}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--grey-700)' }}>Points In</div>
        </div>
        <div style={{ flex: 1, background: 'var(--grey-100)', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800 }}>-{totals.debits}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--grey-700)' }}>Points Out</div>
        </div>
        <div style={{ flex: 1, background: '#1A1A1A', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#FF6200' }}>{transactions.length}</div>
          <div style={{ fontSize: '0.7rem', color: '#aaa' }}>Transactions</div>
        </div>
      </div>

      {loading && <div className="loading-center"><div className="spinner" /></div>}
      {!loading && transactions.length === 0 && <div style={{ textAlign: 'center', color: 'var(--grey-500)', padding: 24 }}>No transactions in this period</div>}
      {!loading && transactions.map(tx => (
        <div key={tx.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--grey-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{tx.first_name} {tx.last_name}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--grey-500)', marginTop: 1 }}>{tx.description || tx.type}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--grey-300)' }}>{format(new Date(tx.created_at), 'd MMM, h:mm aaa')}</div>
          </div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: tx.points_delta > 0 ? 'var(--green)' : 'var(--black)', flexShrink: 0, marginLeft: 12 }}>
            {tx.points_delta > 0 ? '+' : ''}{tx.points_delta}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Slot Manager ─────────────────────────────────────────
const RESOURCES = [
  { type: 'rlt',            label: 'Red Light Therapy',              duration: 25, interval: 25, offset: 0,  nameFilter: null },
  { type: 'cryo',           label: 'Cryotherapy',                    duration: 15, interval: 15, offset: 0,  nameFilter: null },
  { type: 'hbot',           label: 'HBOT — Chamber 1 (on the hour)', duration: 75, interval: 75, offset: 0,  nameFilter: 'Chamber 1' },
  { type: 'hbot',           label: 'HBOT — Chamber 2 (+15 min)',     duration: 75, interval: 75, offset: 15, nameFilter: 'Chamber 2' },
  { type: 'sauna_communal', label: 'Sauna — Communal',               duration: 60, interval: 60, offset: 0,  nameFilter: null },
  { type: 'sauna_private',  label: 'Sauna — Private',                duration: 60, interval: 60, offset: 0,  nameFilter: null },
  { type: 'mat',            label: 'MAT (Muscle Activation)',        duration: 60, interval: 60, offset: 0,  nameFilter: null },
  { type: 'nmt',            label: 'Neuromuscular Therapy (Dave)',   duration: 35, interval: 35, offset: 0, nameFilter: null },
  { type: 'inbody',         label: 'InBody 770 Rescan',              duration: 15, interval: 15, offset: 0,  nameFilter: null },
];

const DAYS = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 0 },
];

// Build hour options: 6:00 to 23:00 in 30-min increments (35 options)
const HOUR_OPTIONS = Array.from({ length: 35 }, (_, i) => 6 + i * 0.5);
function formatHour(h) {
  const hour = Math.floor(h);
  const min = Math.round((h - hour) * 60);
  return `${hour}:${min === 0 ? '00' : min.toString().padStart(2, '0')}`;
}

function SlotManager() {
  const [resourceIdx, setResourceIdx]   = useState(0);
  const [startDate, setStartDate]       = useState('');
  const [endDate, setEndDate]           = useState('');
  const [startHour, setStartHour]       = useState(9);
  const [endHour, setEndHour]           = useState(17);
  const [selectedDays, setSelectedDays] = useState([1,2,3,4,5]); // Mon-Fri default
  const [creating, setCreating]         = useState(false);
  const [result, setResult]             = useState('');

  // Per-slot management
  const [individualDate, setIndividualDate] = useState('');
  const [individualSlots, setIndividualSlots] = useState([]);
  const [loadingIndividual, setLoadingIndividual] = useState(false);
  const [togglingSlotId, setTogglingSlotId] = useState(null);
  const [individualTime, setIndividualTime] = useState(9);
  const [addingSlot, setAddingSlot] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://tb-app-production.up.railway.app/api';

  const resource = RESOURCES[parseInt(resourceIdx)];
  const resourceType = resource?.type;

  function toggleDay(val) {
    setSelectedDays(prev =>
      prev.includes(val) ? prev.filter(d => d !== val) : [...prev, val]
    );
  }

  function applyPreset(days) {
    const today = new Date();
    const end = new Date();
    end.setDate(today.getDate() + days);
    setStartDate(today.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }

  async function createSlots() {
    if (!startDate || !endDate || selectedDays.length === 0) return;
    setCreating(true); setResult('');
    const params = {
      resource_type_param: resourceType,
      start_date: startDate,
      end_date: endDate,
      start_hour: parseFloat(startHour),
      end_hour: parseFloat(endHour),
      interval_mins: resource.interval,
      slot_duration_mins: resource.duration,
      days_of_week: selectedDays,
      start_offset_mins: resource.offset || 0,
    };
    if (resource.nameFilter) params.resource_name_filter = resource.nameFilter;
    const { data, error } = await supabase.rpc('admin_create_slots', params);
    setCreating(false);
    if (error) setResult(`❌ Error: ${error.message}`);
    else setResult(`✅ Created ${data} new slots for ${resource.label}`);
  }

  async function deleteSlots(rangeOnly = false) {
    const label = rangeOnly && startDate
      ? `slots for ${resource.label} between ${startDate} and ${endDate || 'end of range'}`
      : `ALL future unbooked slots for ${resource.label}`;
    if (!window.confirm(`Delete ${label}? Booked slots are never affected.`)) return;
    setCreating(true); setResult('');

    const params = { resource_type_param: resourceType };
    if (rangeOnly && startDate) params.from_date = startDate;
    if (rangeOnly && endDate) params.to_date = endDate;
    if (resource.nameFilter) params.resource_name_filter = resource.nameFilter;

    const { data, error } = await supabase.rpc('admin_delete_future_slots', params);
    setCreating(false);
    if (error) setResult(`❌ Error: ${error.message}`);
    else setResult(`🗑 Deleted ${data} unbooked slots for ${resource.label}`);
  }

  async function setBlockRange(shouldBlock) {
    if (!startDate || !endDate) {
      setResult('❌ Pick a date range first');
      return;
    }
    const sH = parseFloat(startHour);
    const eH = parseFloat(endHour);
    const window_descr = `${formatHour(sH)}–${formatHour(eH)}`;
    if (!window.confirm(
      `${shouldBlock ? 'Block' : 'Unblock'} ${resource.label} slots between ${startDate} and ${endDate}, ${window_descr}, on selected days? ` +
      `Booked slots are never affected.`
    )) return;
    setCreating(true); setResult('');
    const { data, error } = await supabase.rpc('admin_set_slot_block', {
      p_type: resourceType,
      p_from: startDate,
      p_to: endDate,
      p_block: shouldBlock,
      p_start_hour: sH,
      p_end_hour: eH,
      p_days_of_week: selectedDays,
    });
    setCreating(false);
    if (error) setResult(`❌ Error: ${error.message}`);
    else setResult(`${shouldBlock ? '🚫' : '✅'} ${shouldBlock ? 'Blocked' : 'Unblocked'} ${data} slots for ${resource.label}`);
  }

  async function addSingleSlot() {
    if (!individualDate) { setResult('❌ Pick a date first'); return; }
    setAddingSlot(true); setResult('');
    const hour = Math.floor(individualTime);
    const min = Math.round((individualTime - hour) * 60);
    // Build the timestamp in the admin's local (UK) time, matching how the
    // bulk generator stores slot times.
    const startsAt = new Date(`${individualDate}T${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}:00`);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${apiUrl}/admin/slots/single`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          resourceType,
          startsAt: startsAt.toISOString(),
          durationMins: resource.duration,
          ...(resource.nameFilter ? { resourceNameFilter: resource.nameFilter } : {}),
        }),
      });
      const json = await res.json();
      setAddingSlot(false);
      if (!res.ok) { setResult(`❌ ${json.error || 'Could not add slot'}`); return; }
      setResult(`✅ Added a ${resource.label} slot on ${individualDate} at ${formatHour(individualTime)}`);
      loadIndividualSlots(); // refresh the day so it shows immediately
    } catch (err) {
      setAddingSlot(false);
      setResult(`❌ ${err.message || 'Could not add slot'}`);
    }
  }

  async function loadIndividualSlots() {
    if (!individualDate) return;
    setLoadingIndividual(true);
    const { data, error } = await supabase.rpc('admin_list_slots_for_day', {
      p_type: resourceType,
      p_date: individualDate,
    });
    setLoadingIndividual(false);
    if (error) { setResult(`❌ Error loading slots: ${error.message}`); return; }
    setIndividualSlots(data || []);
  }

  async function toggleIndividualSlot(slot) {
    if (slot.booked_count > 0 && !slot.is_blocked) return; // can't block a booked slot
    setTogglingSlotId(slot.id);
    const { data, error } = await supabase.rpc('admin_toggle_slot_block', {
      p_slot_id: slot.id,
      p_block: !slot.is_blocked,
    });
    setTogglingSlotId(null);
    if (error || !data?.ok) {
      setResult(`❌ ${data?.reason === 'has_booking' ? 'Slot has a booking — cannot block' : error?.message || 'Toggle failed'}`);
      return;
    }
    // Update the slot in-place
    setIndividualSlots(prev => prev.map(s =>
      s.id === slot.id ? { ...s, is_blocked: !slot.is_blocked } : s
    ));
  }

  return (
    <div>
      <p style={{ color: 'var(--grey-700)', fontSize: '0.85rem', marginBottom: 16, lineHeight: 1.5 }}>
        Generate slots for any treatment. Existing slots are never duplicated — safe to run repeatedly.
      </p>

      {/* Resource picker */}
      <div className="form-group">
        <label className="form-label">Treatment</label>
        <select className="form-input" value={resourceIdx} onChange={e => setResourceIdx(e.target.value)}>
          {RESOURCES.map((r, i) => <option key={i} value={i}>{r.label}</option>)}
        </select>
      </div>

      {/* Slot info */}
      <div style={{ background: 'var(--grey-100)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.82rem', color: 'var(--grey-700)' }}>
        Slot duration: <strong>{resource.duration} mins</strong> · Interval: <strong>{resource.interval} mins</strong>
      </div>

      {/* Date range */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Start Date</label>
          <input className="form-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">End Date</label>
          <input className="form-input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
      </div>

      {/* Days of week */}
      <div className="form-group">
        <label className="form-label">Days of Week</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DAYS.map(d => (
            <button key={d.value} type="button" onClick={() => toggleDay(d.value)} style={{
              padding: '6px 10px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 700,
              border: `1.5px solid ${selectedDays.includes(d.value) ? 'var(--green)' : 'var(--grey-300)'}`,
              background: selectedDays.includes(d.value) ? 'var(--green-pale)' : '#fff',
              color: selectedDays.includes(d.value) ? 'var(--green)' : 'var(--grey-700)', cursor: 'pointer',
            }}>{d.label}</button>
          ))}
        </div>
      </div>

      {/* Hours */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">From</label>
          <select className="form-input" value={startHour} onChange={e => setStartHour(parseFloat(e.target.value))}>
            {HOUR_OPTIONS.map(h => <option key={h} value={h}>{formatHour(h)}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Until</label>
          <select className="form-input" value={endHour} onChange={e => setEndHour(parseFloat(e.target.value))}>
            {HOUR_OPTIONS.map(h => <option key={h} value={h}>{formatHour(h)}</option>)}
          </select>
        </div>
      </div>

      {/* Quick presets */}
      <div className="form-group">
        <label className="form-label">Quick Presets</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[30, 60, 90].map(days => (
            <button key={days} type="button" onClick={() => applyPreset(days)} style={{
              flex: 1, padding: '8px', borderRadius: 8, fontSize: '0.8rem', fontWeight: 700,
              border: '1.5px solid var(--grey-300)', background: '#fff',
              color: 'var(--grey-700)', cursor: 'pointer',
            }}>Next {days} days</button>
          ))}
        </div>
      </div>

      {result && <div className={`alert ${result.startsWith('✅') ? 'alert-success' : result.startsWith('🗑') ? 'alert-warning' : 'alert-error'}`}>{result}</div>}

      <button className="btn btn-primary btn-full" onClick={createSlots}
        disabled={creating || !startDate || !endDate || selectedDays.length === 0}
        style={{ marginBottom: 10 }}>
        {creating ? 'Working…' : `Generate Slots for ${resource.label}`}
      </button>

      {/* Block / Unblock options */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <button type="button" onClick={() => setBlockRange(true)} disabled={creating || !startDate || !endDate}
          style={{ padding: '11px 8px', borderRadius: 8, border: '1.5px solid #FFE0B2',
            background: '#FFF3E0', color: '#E65100', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
          🚫 Block Date Range
        </button>
        <button type="button" onClick={() => setBlockRange(false)} disabled={creating || !startDate || !endDate}
          style={{ padding: '11px 8px', borderRadius: 8, border: '1.5px solid #C8E6C9',
            background: '#E8F5E9', color: '#2E7D32', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
          ✅ Unblock Date Range
        </button>
      </div>

      {/* Delete options */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <button type="button" onClick={() => deleteSlots(true)} disabled={creating || !startDate}
          style={{ padding: '11px 8px', borderRadius: 8, border: '1.5px solid #FFCDD2',
            background: '#FFEBEE', color: '#D32F2F', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
          🗑 Delete Date Range
        </button>
        <button type="button" onClick={() => deleteSlots(false)} disabled={creating}
          style={{ padding: '11px 8px', borderRadius: 8, border: '1.5px solid #FFCDD2',
            background: '#FFEBEE', color: '#D32F2F', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
          🗑 Delete All Future
        </button>
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', marginTop: 6, textAlign: 'center' }}>
        Block to hide slots from members (reversible). Delete removes them permanently. Booked slots are never affected.
      </div>

      {/* ─── Manage Individual Slots ─── */}
      <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--grey-200)' }}>
        <div className="section-title" style={{ marginBottom: 4 }}>Manage Individual Slots</div>
        <p style={{ color: 'var(--grey-700)', fontSize: '0.82rem', marginBottom: 14, lineHeight: 1.5 }}>
          Pick a day for the selected treatment to see every slot and toggle individual ones on/off. Useful for ad-hoc cancellations.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 12 }}>
          <input className="form-input" type="date" value={individualDate}
            onChange={e => { setIndividualDate(e.target.value); setIndividualSlots([]); }} />
          <button type="button" className="btn btn-primary"
            onClick={loadIndividualSlots}
            disabled={loadingIndividual || !individualDate}>
            {loadingIndividual ? 'Loading…' : 'Load Slots'}
          </button>
        </div>

        {/* Add a single one-off slot for the date chosen above */}
        <div style={{ background: 'var(--green-pale)', border: '1px solid #C8E6C9', borderRadius: 10, padding: 12, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>➕ Add a one-off slot</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, alignItems: 'center' }}>
            <select className="form-input" value={individualTime} onChange={e => setIndividualTime(parseFloat(e.target.value))} style={{ margin: 0 }}>
              {HOUR_OPTIONS.map(h => <option key={h} value={h}>{formatHour(h)}</option>)}
            </select>
            <button type="button" className="btn btn-primary" onClick={addSingleSlot}
              disabled={addingSlot || !individualDate}>
              {addingSlot ? 'Adding…' : 'Add slot'}
            </button>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--grey-700)', marginTop: 8 }}>
            Adds a single {resource.duration}-min {resource.label} slot on the date above — ideal for ad-hoc bookings around the diary, with no need to generate a whole range.
          </div>
        </div>

        {individualSlots.length === 0 && individualDate && !loadingIndividual && (
          <div style={{ textAlign: 'center', color: 'var(--grey-500)', padding: 16, fontSize: '0.85rem' }}>
            No slots for {resource.label} on this date. Generate some first?
          </div>
        )}

        {individualSlots.length > 0 && (
          <div style={{ background: 'var(--grey-100)', borderRadius: 10, padding: 8 }}>
            {individualSlots.map(slot => {
              const time = new Intl.DateTimeFormat('en-GB', {
                timeZone: 'Europe/London', hour: 'numeric', minute: '2-digit', hour12: false,
              }).format(new Date(slot.starts_at));
              const isBooked = slot.booked_count > 0;
              const isBlocked = slot.is_blocked;
              const status = isBooked ? 'Booked' : isBlocked ? 'Blocked' : 'Available';
              const statusColor = isBooked ? 'var(--green)' : isBlocked ? '#E65100' : 'var(--grey-500)';
              const statusBg = isBooked ? 'var(--green-pale)' : isBlocked ? '#FFF3E0' : 'transparent';

              return (
                <div key={slot.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', borderBottom: '1px solid var(--grey-200)', background: '#fff', borderRadius: 6, marginBottom: 4,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', minWidth: 60 }}>{time}</div>
                    <div style={{
                      fontSize: '0.72rem', padding: '2px 8px', borderRadius: 999, fontWeight: 600,
                      color: statusColor, background: statusBg, border: statusBg === 'transparent' ? '1px solid var(--grey-300)' : 'none',
                    }}>{status}</div>
                    {slot.resource_name && resource.nameFilter && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--grey-500)' }}>{slot.resource_name}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleIndividualSlot(slot)}
                    disabled={togglingSlotId === slot.id || (isBooked && !isBlocked)}
                    style={{
                      padding: '6px 12px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700,
                      border: 'none', cursor: (isBooked && !isBlocked) ? 'not-allowed' : 'pointer',
                      background: (isBooked && !isBlocked) ? 'var(--grey-300)' : isBlocked ? '#2E7D32' : '#E65100',
                      color: '#fff', opacity: togglingSlotId === slot.id ? 0.6 : 1,
                    }}>
                    {togglingSlotId === slot.id ? '…' : (isBooked && !isBlocked) ? 'Can\'t block' : isBlocked ? 'Unblock' : 'Block'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Orphaned Payments ─────────────────────────────────────
// Website/email Stripe payments the webhook couldn't auto-credit to a member.
const ORPHAN_REASONS = {
  no_member:              'No matching member for the checkout email',
  no_email:               'No email captured at checkout',
  unrecognised_amount:    'Amount didn\u2019t match a known pack',
  purchase_insert_failed: 'Purchase record failed to save',
  credit_failed:          'Points credit failed (no wallet?)',
};
const ORPHAN_FILTERS = [
  { key: 'unresolved', label: 'Needs action' },
  { key: 'resolved',   label: 'Resolved' },
  { key: 'dismissed',  label: 'Dismissed' },
];

function OrphanRow({ orphan, onResolved }) {
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL || 'https://tb-app-production.up.railway.app/api';

  async function authedPost(path, body) {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${apiUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
      body: JSON.stringify(body || {}),
    });
    return res.json().then(j => ({ ok: res.ok, ...j }));
  }

  async function resolveTo(member) {
    if (saving) return;
    const pts = orphan.points || 0;
    if (!window.confirm(`Credit ${pts} point${pts !== 1 ? 's' : ''} (${orphan.pack_name || 'payment'}) to ${member.first_name} ${member.last_name}?`)) return;
    setSaving(true); setMsg('');
    const result = await authedPost(`/admin/orphaned-payments/${orphan.id}/resolve`, { memberId: member.id });
    setSaving(false);
    if (!result.ok) { setMsg(`\u274c ${result.error || 'Failed'}`); return; }
    onResolved();
  }

  async function dismiss() {
    if (saving) return;
    if (!window.confirm('Dismiss this payment? Use this only if it\u2019s already been handled another way.')) return;
    setSaving(true); setMsg('');
    const result = await authedPost(`/admin/orphaned-payments/${orphan.id}/dismiss`, {});
    setSaving(false);
    if (!result.ok) { setMsg(`\u274c ${result.error || 'Failed'}`); return; }
    onResolved();
  }

  const amount = orphan.amount_pence != null ? `\u00a3${(orphan.amount_pence / 100).toFixed(2)}` : '\u2014';
  const isOpen = orphan.status === 'unresolved';
  const resolvedTo = orphan.resolved_member;

  return (
    <div className="card" style={{ padding: 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: '1rem' }}>
            {amount} {orphan.pack_name ? <span style={{ color: 'var(--grey-700)', fontWeight: 600 }}>· {orphan.pack_name}</span> : null}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--grey-700)', marginTop: 2 }}>
            {orphan.email || 'no email'}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--grey-500)', marginTop: 4 }}>
            {ORPHAN_REASONS[orphan.reason] || orphan.reason} · {format(new Date(orphan.created_at), 'd MMM yyyy, h:mm a')}
          </div>
          {orphan.points > 0 && (
            <div style={{ fontSize: '0.78rem', color: 'var(--green)', fontWeight: 700, marginTop: 4 }}>
              {orphan.points} points to credit
            </div>
          )}
          {!isOpen && resolvedTo && (
            <div style={{ fontSize: '0.78rem', color: 'var(--grey-500)', marginTop: 4 }}>
              {orphan.status === 'resolved' ? 'Credited to' : 'Dismissed'} {resolvedTo ? `${resolvedTo.first_name} ${resolvedTo.last_name}` : ''}
            </div>
          )}
        </div>
        <span style={{
          fontSize: '0.68rem', fontWeight: 700, padding: '3px 8px', borderRadius: 999, flexShrink: 0,
          background: isOpen ? '#FFF3E0' : orphan.status === 'resolved' ? 'var(--green-pale)' : 'var(--grey-100)',
          color: isOpen ? '#E65100' : orphan.status === 'resolved' ? 'var(--green)' : 'var(--grey-500)',
        }}>
          {isOpen ? 'NEEDS ACTION' : orphan.status.toUpperCase()}
        </span>
      </div>

      {msg && <div style={{ fontSize: '0.8rem', marginTop: 8 }}>{msg}</div>}

      {isOpen && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--grey-100)', paddingTop: 12 }}>
          {!picking ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setPicking(true)} disabled={saving} style={{
                flex: 1, padding: '9px 12px', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem',
                border: 'none', background: '#FF6200', color: '#fff', cursor: 'pointer',
              }}>Credit to a member</button>
              <button type="button" onClick={dismiss} disabled={saving} style={{
                padding: '9px 12px', borderRadius: 8, fontWeight: 600, fontSize: '0.82rem',
                border: '1.5px solid var(--grey-300)', background: '#fff', color: 'var(--grey-700)', cursor: 'pointer',
              }}>Dismiss</button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--grey-700)', marginBottom: 6, fontWeight: 600 }}>
                Who should get these points?
              </div>
              <MemberSearch onSelect={resolveTo} />
              <button type="button" onClick={() => setPicking(false)} style={{
                marginTop: 8, padding: '6px 12px', borderRadius: 8, fontSize: '0.78rem',
                border: '1.5px solid var(--grey-300)', background: '#fff', color: 'var(--grey-700)', cursor: 'pointer',
              }}>Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OrphanedPayments() {
  const [filter, setFilter] = useState('unresolved');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://tb-app-production.up.railway.app/api';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${apiUrl}/admin/orphaned-payments?status=${filter}`, {
          headers: { 'Authorization': `Bearer ${session?.access_token}` },
        });
        const json = await res.json();
        if (!cancelled) setPayments(json.payments || []);
      } catch {
        if (!cancelled) setPayments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filter, reloadKey, apiUrl]);

  return (
    <div>
      <div className="section-title">Payments needing attention</div>
      <p style={{ fontSize: '0.82rem', color: 'var(--grey-500)', margin: '0 0 12px' }}>
        Website or email purchases Stripe took but couldn&rsquo;t automatically match to a member.
        Credit them to the right person here.
      </p>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {ORPHAN_FILTERS.map(f => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)} style={{
            padding: '6px 12px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600,
            border: `1.5px solid ${filter === f.key ? 'var(--green)' : 'var(--grey-300)'}`,
            background: filter === f.key ? 'var(--green-pale)' : '#fff',
            color: filter === f.key ? 'var(--green)' : 'var(--grey-700)', cursor: 'pointer',
          }}>{f.label}</button>
        ))}
      </div>

      {loading && <div className="loading-center"><div className="spinner" /></div>}
      {!loading && payments.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--grey-500)', padding: 24 }}>
          {filter === 'unresolved' ? '\u2713 No payments need attention' : `No ${filter} payments`}
        </div>
      )}
      {!loading && payments.map(o => (
        <OrphanRow key={o.id} orphan={o} onResolved={() => setReloadKey(k => k + 1)} />
      ))}
    </div>
  );
}

// ── Main Admin Page ───────────────────────────────────────
// ── Send a push notification (one member or everyone) ─────
function Notifications() {
  const [target, setTarget] = useState('all');        // 'all' | 'member'
  const [member, setMember] = useState(null);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState('');

  const canSend = title.trim() && message.trim() && (target === 'all' || member) && !sending;

  async function handleSend() {
    setSending(true); setResult('');
    const apiUrl = process.env.REACT_APP_API_URL || 'https://tb-app-production.up.railway.app/api';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${apiUrl}/admin/push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          target,
          memberId: target === 'member' ? member?.id : undefined,
          title: title.trim(),
          body: message.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setResult(`❌ ${data.error || 'Failed to send'}`);
      } else if (data.sent === 0) {
        setResult(target === 'all'
          ? '⚠️ Sent, but nobody has notifications enabled yet — 0 devices reached.'
          : "⚠️ This member hasn't enabled notifications, so nothing was delivered.");
      } else {
        const who = target === 'all' ? `${data.recipients} member${data.recipients !== 1 ? 's' : ''}` : 'this member';
        setResult(`✅ Delivered to ${data.sent} device${data.sent !== 1 ? 's' : ''} across ${who}.`);
        setTitle(''); setMessage('');
      }
    } catch (err) {
      setResult(`❌ ${err.message}`);
    }
    setSending(false);
  }

  return (
    <div>
      <p style={{ color: 'var(--grey-700)', fontSize: '0.85rem', marginBottom: 16, lineHeight: 1.5 }}>
        Send a notification to a single member or to everyone with notifications enabled. Only members who've turned on
        notifications (in Profile) will receive it.
      </p>

      {/* Recipient */}
      <div className="form-group">
        <label className="form-label">Send to</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {[{ key: 'all', label: '👥 Everyone' }, { key: 'member', label: '👤 One member' }].map(o => (
            <button key={o.key} type="button" onClick={() => { setTarget(o.key); setResult(''); }} style={{
              padding: '10px 6px', borderRadius: 10, fontWeight: 700, fontSize: '0.85rem',
              border: `2px solid ${target === o.key ? 'var(--green)' : 'var(--grey-300)'}`,
              background: target === o.key ? 'var(--green-pale)' : '#fff',
              color: target === o.key ? 'var(--green)' : 'var(--grey-700)', cursor: 'pointer',
            }}>{o.label}</button>
          ))}
        </div>
      </div>

      {target === 'member' && (
        <div className="form-group">
          <label className="form-label">Member</label>
          {member ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: 'var(--grey-100)', borderRadius: 8, padding: '10px 14px' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{member.first_name} {member.last_name}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--grey-500)' }}>{member.email}</div>
              </div>
              <button type="button" onClick={() => setMember(null)} style={{ border: 'none', background: 'none', color: 'var(--green)', fontWeight: 700, cursor: 'pointer' }}>Change</button>
            </div>
          ) : (
            <MemberSearch onSelect={m => setMember(m)} />
          )}
        </div>
      )}

      {/* Title */}
      <div className="form-group">
        <label className="form-label">Title</label>
        <input className="form-input" value={title} maxLength={100}
          placeholder="e.g. Action needed: set your app password"
          onChange={e => setTitle(e.target.value)} />
      </div>

      {/* Message */}
      <div className="form-group">
        <label className="form-label">Message</label>
        <textarea className="form-input" value={message} maxLength={300} rows={3}
          placeholder="Keep it short — long messages get cut off on the lock screen."
          onChange={e => setMessage(e.target.value)} style={{ resize: 'vertical' }} />
        <div style={{ fontSize: '0.75rem', color: 'var(--grey-500)', textAlign: 'right', marginTop: 4 }}>{message.length}/300</div>
      </div>

      {result && (
        <div className={`alert ${result.startsWith('✅') ? 'alert-success' : result.startsWith('⚠️') ? 'alert-warning' : 'alert-error'}`} style={{ marginBottom: 12 }}>
          {result}
        </div>
      )}

      <button type="button" className="btn btn-primary btn-full" onClick={handleSend} disabled={!canSend}>
        {sending ? 'Sending…' : (target === 'all' ? '🔔 Send to everyone' : '🔔 Send notification')}
      </button>
    </div>
  );
}

// ── Biohack Day opener ───────────────────────────────────
// Biohack has its own standalone availability, separate from the normal station
// calendar. Opening a day just adds start times members can book; it never
// touches regular treatment availability. Booked slots stay open — remove the
// ones you don't want by hand from the list below.
const BIOHACK_START_TIMES = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30'];

function BiohackDayManager() {
  const [date, setDate]       = useState('');
  const [selected, setSelected] = useState([]);
  const [saving, setSaving]   = useState(false);
  const [result, setResult]   = useState('');
  const [openSlots, setOpenSlots] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL || 'https://tb-app-production.up.railway.app/api';

  function toggle(t) {
    setSelected(prev => (prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]));
  }

  const loadSlots = React.useCallback(async (d) => {
    if (!d) { setOpenSlots([]); return; }
    setLoadingList(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${apiUrl}/admin/biohack-slots?date=${d}`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      setOpenSlots(res.ok ? (data.slots || []) : []);
    } catch (e) {
      setOpenSlots([]);
    }
    setLoadingList(false);
  }, [apiUrl]);

  useEffect(() => { loadSlots(date); }, [date, loadSlots]);

  async function openDay() {
    if (!date || selected.length === 0) { setResult('❌ Pick a date and at least one start time'); return; }
    setSaving(true); setResult('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${apiUrl}/admin/biohack-slots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ date, startTimes: [...selected].sort() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setResult(`❌ ${data.error || 'Failed to open Biohack day'}`);
      } else {
        const n = data.openedStarts?.length || 0;
        setResult(
          `✅ Opened ${n} start time${n === 1 ? '' : 's'} on ${date}` +
          (data.skipped ? ` (${data.skipped} already open).` : '.')
        );
        setSelected([]);
        loadSlots(date);
      }
    } catch (e) {
      setResult(`❌ ${e.message}`);
    }
    setSaving(false);
  }

  async function deleteSlot(id) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${apiUrl}/admin/biohack-slots/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (res.ok) setOpenSlots(prev => prev.filter(s => s.id !== id));
    } catch (e) { /* no-op */ }
  }

  return (
    <div className="card" style={{ padding: 20, marginBottom: 20 }}>
      <h3 style={{ fontWeight: 800, marginBottom: 4 }}>⚗️ Open a Biohack Day</h3>
      <p style={{ color: 'var(--grey-500)', fontSize: '0.82rem', marginBottom: 16, lineHeight: 1.5 }}>
        Pick a date and the start times to offer. These are Biohack-only and don't affect the normal
        booking calendar. The same times are offered for every option — Gold/Silver, Solo/For Two —
        and you arrange the stations yourself.
      </p>

      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: 'var(--grey-700)', marginBottom: 6 }}>Date</label>
      <input type="date" value={date} onChange={e => setDate(e.target.value)}
        style={{ width: '100%', padding: 10, borderRadius: 10, border: '2px solid var(--grey-300)', marginBottom: 16 }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--grey-700)' }}>Start times</label>
        <div style={{ display: 'flex', gap: 14 }}>
          <button type="button" onClick={() => setSelected(BIOHACK_START_TIMES)}
            style={{ background: 'none', border: 'none', color: 'var(--green)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>All</button>
          <button type="button" onClick={() => setSelected([])}
            style={{ background: 'none', border: 'none', color: 'var(--grey-500)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>Clear</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 18 }}>
        {BIOHACK_START_TIMES.map(t => {
          const on = selected.includes(t);
          return (
            <button key={t} type="button" onClick={() => toggle(t)} style={{
              padding: '10px 4px', borderRadius: 10, fontWeight: 700, fontSize: '0.85rem',
              border: `2px solid ${on ? 'var(--green)' : 'var(--grey-300)'}`,
              background: on ? 'var(--green-pale)' : '#fff',
              color: on ? 'var(--green)' : 'var(--grey-700)', cursor: 'pointer',
            }}>{t}</button>
          );
        })}
      </div>

      <button className="btn btn-primary btn-full" onClick={openDay} disabled={saving || !date || selected.length === 0}>
        {saving ? 'Opening…' : 'Open Biohack Availability'}
      </button>
      {result && <div style={{ marginTop: 12, fontSize: '0.85rem', lineHeight: 1.5 }}>{result}</div>}

      {date && (
        <div style={{ marginTop: 20, borderTop: '1px solid var(--grey-100)', paddingTop: 16 }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: 'var(--grey-700)', marginBottom: 10 }}>
            Open times on {date}
          </label>
          {loadingList ? (
            <div style={{ fontSize: '0.82rem', color: 'var(--grey-500)' }}>Loading…</div>
          ) : openSlots.length === 0 ? (
            <div style={{ fontSize: '0.82rem', color: 'var(--grey-500)' }}>No Biohack times open on this date yet.</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {openSlots.map(s => (
                <span key={s.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                  borderRadius: 999, border: '2px solid var(--grey-300)', fontSize: '0.85rem', fontWeight: 700,
                }}>
                  {s.start_time}
                  <button type="button" onClick={() => deleteSlot(s.id)} title="Remove this start time"
                    style={{ background: 'none', border: 'none', color: 'var(--red, #c0392b)', fontWeight: 800, fontSize: '1rem', cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Training: assign a phase (workouts + exercises) to a member ──────────
const TB_BODY_PART_ORDER = ['Chest', 'Back', 'Shoulders', 'Quads', 'Hamstrings', 'Glutes', 'Calves', 'Biceps', 'Triceps', 'Core', 'Full Body', 'Cardio'];
const tbBlankExercise = () => ({ code: '', name: '', sets: '', reps: '', tempo: '', rest_seconds: '', notes: '', target_weight: '', t_time: '', t_distance: '', t_resistance: '', t_calories: '', _custom: false, rel: '' });
const tbBlankProgramme = (n) => ({ name: n, kind: 'workout', placement: 'standalone', exercises: [tbBlankExercise()] });
const tbBlankMobilityProgramme = (n) => ({ name: n, kind: 'mobility', placement: 'standalone', exercises: [tbBlankExercise()] });

// ── Programme exercise codes (A1, A2, B1 …) ────────────────────────────
// A letter = one superset group, the number = position within it. After A1
// the only valid next codes are A2 (same group) or B1 (new group). We store
// each exercise's relation to the one above ('base'|'same'|'new') and derive
// the real code, so a code can never be mistyped.
function tbParseCode(code) {
  const m = String(code || '').trim().match(/^([A-Za-z]+)\s*0*(\d+)$/);
  return m ? { letter: m[1].toUpperCase(), num: parseInt(m[2], 10) } : null;
}
function tbNextLetter(letter) {
  const arr = String(letter || 'A').toUpperCase().split('');
  let i = arr.length - 1;
  while (i >= 0) {
    if (arr[i] === 'Z') { arr[i] = 'A'; i--; }
    else { arr[i] = String.fromCharCode(arr[i].charCodeAt(0) + 1); return arr.join(''); }
  }
  return 'A' + arr.join('');
}
function tbNextCodeOptions(prevCode) {
  const p = tbParseCode(prevCode);
  if (!p) return ['A1', 'A1'];
  return [`${p.letter}${p.num + 1}`, `${tbNextLetter(p.letter)}1`];
}
function tbComputeCodes(exercises) {
  let letter = 'A', num = 1;
  return (exercises || []).map((e, i) => {
    if (i === 0) { letter = 'A'; num = 1; }
    else if (e.rel === 'new') { letter = tbNextLetter(letter); num = 1; }
    else { num += 1; }
    return { ...e, code: `${letter}${num}` };
  });
}
function tbWithRels(exercises) {
  let prev = null;
  return (exercises || []).map((e, i) => {
    const cur = tbParseCode(e.code);
    let rel;
    if (i === 0) rel = 'base';
    else if (prev && cur && cur.letter === prev.letter && cur.num === prev.num + 1) rel = 'same';
    else rel = 'new';
    prev = cur || prev;
    return { ...e, rel };
  });
}

// Epley estimated 1-rep max
function tbE1rm(w, reps) {
  if (!w || !reps || Number.isNaN(w) || Number.isNaN(reps)) return null;
  return w * (1 + reps / 30);
}
const tbRound1 = (n) => (n == null ? null : Math.round(n * 10) / 10);

// Map a loaded phase (from admin_get_member_training) into the editable form
// shape used by the builder.
function tbPhaseToForm(ph) {
  const progs = (ph.programmes || []).filter(pr => pr.kind !== 'mobility').map(pr => ({
    name: pr.name || 'Workout',
    kind: 'workout',
    placement: 'standalone',
    exercises: tbWithRels((pr.exercises || []).map(e => ({
      code: e.code || '',
      name: e.name || '',
      sets: e.sets == null ? '' : String(e.sets),
      reps: e.reps || '',
      tempo: e.tempo || '',
      rest_seconds: e.rest_seconds == null ? '' : String(e.rest_seconds),
      notes: e.notes || '',
      target_weight: e.target_weight == null ? '' : String(e.target_weight),
      t_time: e.t_time == null ? '' : String(e.t_time),
      t_distance: e.t_distance == null ? '' : String(e.t_distance),
      t_resistance: e.t_resistance == null ? '' : String(e.t_resistance),
      t_calories: e.t_calories == null ? '' : String(e.t_calories),
      _custom: false,
    }))),
  }));
  return progs.length ? progs : [tbBlankProgramme('1A')];
}

// The single mobility programme on a phase, if any.
function tbPhaseMobProgramme(ph) {
  return ((ph && ph.programmes) || []).find(pr => pr.kind === 'mobility') || null;
}
// Stretch list for the blue editor — from a mobility programme if present,
// else the legacy phase-level mobility jsonb.
function tbPhaseToMobility(ph) {
  if (!ph) return [];
  const mp = tbPhaseMobProgramme(ph);
  if (mp && (mp.exercises || []).length) {
    return mp.exercises.map(e => ({ name: e.name || '', mode: 'single', amount: e.reps || '', left: '', right: '' }));
  }
  return Array.isArray(ph.mobility) ? ph.mobility : [];
}

// Tiny dependency-free SVG sparkline for the check-in metric trends.
function TbSparkline({ values, color = '#FF6200', lowerIsBetter = false, unit = '' }) {
  const vals = (values || []).filter(v => v != null && !Number.isNaN(v));
  if (vals.length === 0) {
    return <div style={{ fontSize: '0.72rem', color: 'var(--grey-400, #aaa)' }}>No data</div>;
  }
  const W = 120, H = 34, pad = 3;
  let min = Math.min(...vals), max = Math.max(...vals);
  if (min === max) { min -= 1; max += 1; }
  const n = vals.length;
  const x = i => pad + (n === 1 ? (W - 2 * pad) / 2 : (i / (n - 1)) * (W - 2 * pad));
  const y = v => pad + (H - 2 * pad) - ((v - min) / (max - min)) * (H - 2 * pad);
  const d = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const first = vals[0], last = vals[vals.length - 1];
  const delta = last - first;
  const improved = lowerIsBetter ? delta < 0 : delta > 0;
  const deltaColor = delta === 0 ? 'var(--grey-500)' : improved ? '#2E7D32' : '#C62828';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <svg width={W} height={H} style={{ flexShrink: 0 }}>
        <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={x(n - 1)} cy={y(last)} r="2.5" fill={color} />
      </svg>
      <div style={{ fontSize: '0.78rem', lineHeight: 1.2 }}>
        <div style={{ fontWeight: 800 }}>{tbRound1(last)}{unit}</div>
        {n > 1 && (
          <div style={{ color: deltaColor, fontWeight: 700, fontSize: '0.72rem' }}>
            {delta > 0 ? '+' : ''}{tbRound1(delta)}{unit}
          </div>
        )}
      </div>
    </div>
  );
}

// Stretches & mobilisations editor for a phase (coach side). Defined once
// per phase; shows before every session for the member. No completion logged.
const tbBlankMobility = () => ({ name: '', mode: 'single', amount: '', left: '', right: '' });
// Starter list of common stretches & mobilisations offered as a dropdown.
// It's a datalist, so staff can still type anything custom.
const TB_MOBILITY_SUGGESTIONS = [
  '90-90 hip stretch', "World's greatest stretch", 'Couch stretch', 'Hip flexor stretch',
  'Pigeon stretch', 'Glute stretch (figure-4)', 'Adductor rock back', 'Cossack squat',
  'Deep squat hold', 'Spiderman lunge', 'Hamstring stretch', 'Standing toe touch',
  'Calf stretch', 'Quad stretch', 'Ankle mobility (knee-to-wall)',
  'Thoracic rotation', 'Cat-cow', 'Thread the needle', 'Child\u2019s pose',
  'Cobra stretch', 'Scorpion stretch', 'Lat stretch', 'Doorway pec stretch',
  'Band shoulder dislocates', 'Wall slides', 'Neck stretch', 'Wrist mobility',
  'Downward dog', 'Seated forward fold',
];
function TbMobilityEditor({ items, onChange, inProgramme, onInProgrammeChange }) {
  const list = items || [];
  const update = (i, patch) => onChange(list.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const add = () => onChange([...list, tbBlankMobility()]);
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const inp = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--grey-300)', background: '#fff', fontSize: '0.9rem' };
  const lbl = { fontSize: '0.7rem', fontWeight: 700, color: 'var(--grey-500)', marginBottom: 3 };

  return (
    <div style={{ background: '#EEF6FF', borderRadius: 10, padding: 12, border: '1px solid #BBDCF8' }}>
      <datalist id="tb-admin-mobility-suggestions">
        {TB_MOBILITY_SUGGESTIONS.map(s => <option key={s} value={s} />)}
      </datalist>
      <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1565C0', marginBottom: 2 }}>🤸 Stretching &amp; Mobility</div>
      <div style={{ fontSize: '0.72rem', color: 'var(--grey-600, #555)', marginBottom: 10 }}>
        Add any stretches or mobilisations for this phase. Members tick each one off and can log a hold.
      </div>

      {list.map((m, i) => (
        <div key={i} style={{ background: '#fff', border: '1px solid #D7EBFB', borderRadius: 8, padding: 10, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input style={{ ...inp, flex: 1 }} value={m.name} list="tb-admin-mobility-suggestions"
              onChange={e => update(i, { name: e.target.value })} placeholder="Pick or type a stretch / mobilisation" />
            <button type="button" onClick={() => remove(i)} style={{ color: 'var(--danger)', fontSize: '1.1rem', lineHeight: 1 }} aria-label="Remove">×</button>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.78rem', color: 'var(--grey-700)', marginBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={m.mode === 'split'}
              onChange={e => update(i, { mode: e.target.checked ? 'split' : 'single' })} />
            Different left &amp; right amounts
          </label>

          {m.mode === 'split' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label style={{ display: 'block' }}>
                <div style={lbl}>Left</div>
                <input style={inp} value={m.left} onChange={e => update(i, { left: e.target.value })} placeholder="e.g. 45s" />
              </label>
              <label style={{ display: 'block' }}>
                <div style={lbl}>Right</div>
                <input style={inp} value={m.right} onChange={e => update(i, { right: e.target.value })} placeholder="e.g. 30s" />
              </label>
            </div>
          ) : (
            <label style={{ display: 'block' }}>
              <div style={lbl}>Amount</div>
              <input style={inp} value={m.amount} onChange={e => update(i, { amount: e.target.value })} placeholder="e.g. 45s, 10 reps, 3 breaths" />
            </label>
          )}
        </div>
      ))}

      <button type="button" onClick={add} style={{ color: '#1565C0', fontWeight: 700, fontSize: '0.84rem', marginTop: 2 }}>+ Add a stretch</button>

      {list.length > 0 && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '1px solid #D7EBFB', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--grey-700)' }}>
          <input type="checkbox" checked={!!inProgramme}
            onChange={e => onInProgrammeChange(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            <strong>Add to the programme</strong> — shows before every workout in this phase.
            <span style={{ color: 'var(--grey-500)' }}> Leave unticked to perform it as its own session.</span>
          </span>
        </label>
      )}
    </div>
  );
}

// Searchable exercise picker for the admin programme builder. A bottom sheet
// with a search box at the very top — start typing and the matching exercises
// surface right away — grouped by body part, each part offering a custom
// option, plus a free-text custom at the bottom. Mirrors the member picker.
function TbExercisePicker({ orderedParts, grouped, value, isMob, onPick, onClose }) {
  const [q, setQ] = useState('');
  const pInput = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--grey-300)', background: '#fff', fontSize: '0.95rem', boxSizing: 'border-box' };
  const query = q.trim().toLowerCase();
  const sections = orderedParts
    .map(part => [part, (grouped[part] || []).filter(ex => !query || (ex.name || '').toLowerCase().includes(query))])
    .filter(([, list]) => list.length > 0 || !query);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={ev => ev.stopPropagation()} style={{ background: '#fff', width: '100%', maxWidth: 520, maxHeight: '88vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, display: 'flex', flexDirection: 'column', boxShadow: '0 -4px 24px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '14px 16px 8px', borderBottom: '1px solid var(--grey-200, #eee)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 800, fontSize: '1rem' }}>Choose {isMob ? 'a stretch' : 'an exercise'}</div>
            <button type="button" onClick={onClose} style={{ color: 'var(--grey-500)', fontSize: '1.1rem', lineHeight: 1 }} aria-label="Close">✕</button>
          </div>
          <input style={pInput} value={q} autoFocus onChange={e => setQ(e.target.value)} placeholder={`🔍 Search ${isMob ? 'stretches' : 'exercises'}…`} />
        </div>
        <div style={{ overflowY: 'auto', padding: '12px 16px', flex: 1 }}>
          {sections.length === 0 && (
            <div style={{ color: 'var(--grey-500)', fontSize: '0.88rem', padding: '8px 0' }}>No matches for “{q}”.</div>
          )}
          {sections.map(([part, list]) => (
            <div key={part} style={{ marginBottom: 14 }}>
              <div style={{ display: 'inline-block', fontSize: '0.62rem', fontWeight: 800, color: 'var(--grey-700)', background: 'var(--grey-100)', borderRadius: 999, padding: '2px 10px', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>{part}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {list.map(ex => {
                  const sel = value && ex.name === value;
                  return (
                    <button type="button" key={ex.id} onClick={() => onPick(ex.name)}
                      style={{ fontSize: '0.84rem', fontWeight: 600, padding: '7px 11px', borderRadius: 8, cursor: 'pointer', color: sel ? '#fff' : 'var(--green)', background: sel ? 'var(--green)' : '#fff', border: '1px solid var(--green)' }}>
                      {sel ? '✓ ' : ''}{ex.name}
                    </button>
                  );
                })}
                <button type="button" key="__add__" onClick={() => onPick(`__custom__::${part}`)}
                  style={{ fontSize: '0.84rem', fontWeight: 700, padding: '7px 11px', borderRadius: 8, cursor: 'pointer', color: 'var(--green)', background: '#fff', border: '1px dashed var(--green)' }}>
                  ＋ Custom {part}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--grey-200, #eee)' }}>
          <button type="button" onClick={() => onPick('__custom__')}
            style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid var(--grey-300)', background: 'var(--grey-50)', fontWeight: 700, fontSize: '0.86rem', color: 'var(--grey-700)', cursor: 'pointer' }}>
            ✏️ Type a custom {isMob ? 'stretch' : 'exercise'} instead
          </button>
        </div>
      </div>
    </div>
  );
}

function TrainingAssign() {
  const [members, setMembers] = useState([]);
  const [search, setSearch]   = useState('');
  const [target, setTarget]   = useState(null); // { id, name, email }
  const [existing, setExisting] = useState([]); // member's current phases
  const [phaseName, setPhaseName] = useState('');
  const [mobility, setMobility] = useState([]); // stretches & mobilisations for the phase
  const [mobInProgramme, setMobInProgramme] = useState(true); // true = before every workout
  const [programmes, setProgrammes] = useState([tbBlankProgramme('1A')]);
  const [editingPhaseId, setEditingPhaseId] = useState(null); // null = creating
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const [error, setError]     = useState('');
  // Build-ahead-by-email: write a programme for someone who hasn't signed up
  // yet. It waits on their email and attaches itself when they create an account.
  const [emailMode, setEmailMode] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');
  const [pending, setPending]     = useState([]); // programmes waiting on an email
  const [pickerFor, setPickerFor] = useState(null); // { pi, ei, isMob } — open exercise picker

  function loadPending() {
    return supabase.rpc('admin_list_pending_phases').then(({ data }) => setPending(data || []));
  }

  // Shared exercise library (same list members see). Staff pick from it, and
  // any custom exercise they add flows into everyone's dropdowns.
  const [catalogue, setCatalogue] = useState([]);
  const [catLoaded, setCatLoaded] = useState(false);
  const [libOpen, setLibOpen]     = useState(false);
  const [newExName, setNewExName] = useState('');
  const [newExPart, setNewExPart] = useState('Chest');
  const [newExLoad, setNewExLoad] = useState('weighted');
  const [libErr, setLibErr]       = useState('');
  const [libSaving, setLibSaving] = useState(false);

  function loadCatalogue() {
    return supabase.rpc('get_exercise_catalogue').then(({ data }) => {
      setCatalogue(Array.isArray(data) ? data : (data || []));
      setCatLoaded(true);
    });
  }

  useEffect(() => {
    supabase.rpc('admin_list_members').then(({ data }) => setMembers(data || []));
    loadCatalogue();
    loadPending();
  }, []);

  // When a member is chosen, load what training they already have.
  useEffect(() => {
    if (!target) { setExisting([]); return; }
    supabase.rpc('admin_get_member_training', { target_member: target.id })
      .then(({ data }) => setExisting(data || []));
  }, [target]);

  const grouped = useMemo(() => {
    const g = {};
    for (const ex of catalogue) {
      const part = ex.body_part || 'Other';
      (g[part] = g[part] || []).push(ex);
    }
    return g;
  }, [catalogue]);
  const orderedParts = useMemo(() => {
    const parts = Object.keys(grouped);
    return parts.sort((a, b) => {
      const ia = TB_BODY_PART_ORDER.indexOf(a), ib = TB_BODY_PART_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [grouped]);
  const nameSet = useMemo(() => new Set(catalogue.map(e => e.name)), [catalogue]);
  // Name → body part, so the builder can show cardio-specific target boxes.
  const partByName = useMemo(() => {
    const m = {};
    for (const c of catalogue) if (c.name) m[c.name] = c.body_part;
    return m;
  }, [catalogue]);

  // Once the catalogue loads, mark any exercise name that isn't in the library
  // as "custom" so its text box shows (used when editing an existing phase).
  useEffect(() => {
    if (!catLoaded) return;
    setProgrammes(prev => prev.map(p => ({
      ...p,
      exercises: p.exercises.map(e => (
        e.name && !nameSet.has(e.name) ? { ...e, _custom: true } : e
      )),
    })));
  }, [catLoaded, nameSet]);

  async function addToLibrary() {
    const nm = newExName.trim();
    if (!nm) { setLibErr('Type an exercise name.'); return; }
    setLibSaving(true); setLibErr('');
    const { error: e } = await supabase.rpc('add_exercise_to_catalogue', { p_name: nm, p_body_part: newExPart, p_load_type: newExLoad });
    setLibSaving(false);
    if (e) { setLibErr(e.message || 'Could not add.'); return; }
    setNewExName('');
    setNewExLoad('weighted');
    loadCatalogue();
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members.slice(0, 8);
    return members.filter(m =>
      (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
    ).slice(0, 8);
  }, [members, search]);

  function updateProg(pi, field, value) {
    setProgrammes(prev => prev.map((p, i) => (i === pi ? { ...p, [field]: value } : p)));
  }
  function addProg() { setProgrammes(prev => [...prev, tbBlankProgramme(`${prev.length + 1} new`)]); }
  function removeProg(pi) { setProgrammes(prev => prev.filter((_, i) => i !== pi)); }
  function updateEx(pi, ei, field, value) {
    setProgrammes(prev => prev.map((p, i) => {
      if (i !== pi) return p;
      return { ...p, exercises: p.exercises.map((e, j) => (j === ei ? { ...e, [field]: value } : e)) };
    }));
  }
  function addEx(pi) {
    setProgrammes(prev => prev.map((p, i) => i === pi ? { ...p, exercises: [...p.exercises, tbBlankExercise()] } : p));
  }
  function removeEx(pi, ei) {
    setProgrammes(prev => prev.map((p, i) => i === pi ? { ...p, exercises: p.exercises.filter((_, j) => j !== ei) } : p));
  }

  // Exercise name dropdown change: a real name, or "type a custom one".
  async function onSelectName(pi, ei, value) {
    // "＋ Add custom <part>…" — add it to the library under that body part so
    // it lands in that part of the list from now on, then select it.
    if (typeof value === 'string' && value.startsWith('__custom__::')) {
      const part = value.slice('__custom__::'.length);
      const typed = (typeof window !== 'undefined' && window.prompt)
        ? window.prompt(`New ${part} exercise name:`) : '';
      const nm = (typed || '').trim();
      if (!nm) return;
      try { await supabase.rpc('add_exercise_to_catalogue', { p_name: nm, p_body_part: part }); } catch (_) {}
      await loadCatalogue();
      setProgrammes(prev => prev.map((p, i) => {
        if (i !== pi) return p;
        return { ...p, exercises: p.exercises.map((e, j) => (j === ei ? { ...e, name: nm, _custom: false } : e)) };
      }));
      return;
    }
    if (value === '__custom__') {
      updateEx(pi, ei, '_custom', true);
      updateEx(pi, ei, 'name', '');
    } else {
      setProgrammes(prev => prev.map((p, i) => {
        if (i !== pi) return p;
        return { ...p, exercises: p.exercises.map((e, j) => (j === ei ? { ...e, name: value, _custom: false } : e)) };
      }));
    }
  }

  function resetForm() {
    setPhaseName(''); setMobility([]); setMobInProgramme(true); setProgrammes([tbBlankProgramme('1A')]); setEditingPhaseId(null);
    setEmailMode(false); setEmailAddr('');
  }

  async function deletePending(p) {
    if (!window.confirm(`Cancel the waiting programme for ${p.email}?`)) return;
    await supabase.rpc('admin_delete_pending_phase', { p_id: p.id });
    loadPending();
  }

  // Pull an existing phase's workouts into the builder as editable cards.
  function phaseToProgCards(ph) {
    return tbPhaseToForm(ph).map(p => ({
      ...p,
      exercises: p.exercises.map(e => ({ ...e, _custom: e.name ? !nameSet.has(e.name) : false })),
    }));
  }

  // Load an existing phase into the builder to edit it.
  function editPhase(ph) {
    setEditingPhaseId(ph.id);
    setPhaseName(ph.name || '');
    setMobility(tbPhaseToMobility(ph));
    const mp = tbPhaseMobProgramme(ph);
    setMobInProgramme(mp ? mp.placement === 'preworkout' : true);
    setProgrammes(phaseToProgCards(ph));
    setMsg(''); setError('');
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Load an existing phase AND drop a fresh empty workout card on the end, so
  // "add a workout to this phase" is a single, obvious action (rather than
  // accidentally building a whole new phase). Scrolls to the new card.
  function addWorkoutToPhase(ph) {
    setEditingPhaseId(ph.id);
    setPhaseName(ph.name || '');
    setMobility(tbPhaseToMobility(ph));
    const mp = tbPhaseMobProgramme(ph);
    setMobInProgramme(mp ? mp.placement === 'preworkout' : true);
    const progs = phaseToProgCards(ph);
    const existingWorkouts = progs.filter(p => p.kind !== 'mobility').length;
    setProgrammes([...progs, tbBlankProgramme(`${existingWorkouts + 1} new`)]);
    setMsg(''); setError('');
    if (typeof window !== 'undefined') {
      setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 120);
    }
  }

  async function assign() {
    setSaving(true); setError(''); setMsg('');
    if (emailMode) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddr.trim())) {
        setError('Enter a valid email address.'); setSaving(false); return;
      }
    } else if (!target) { setError('Pick a member first.'); setSaving(false); return; }
    if (!phaseName.trim()) { setError('Give the phase a name.'); setSaving(false); return; }
    // Every exercise after the first in a workout must have a deliberate
    // superset choice (same / new) — no lazy defaults. Only named rows count.
    for (let pi = 0; pi < programmes.length; pi++) {
      const named = programmes[pi].exercises.filter(e => (e.name || '').trim());
      for (let k = 1; k < named.length; k++) {
        if (named[k].rel !== 'same' && named[k].rel !== 'new') {
          setError(`In “${(programmes[pi].name || '').trim() || `Workout ${pi + 1}`}”, choose “same” or “new” for every exercise after the first (the orange “Group?” boxes).`);
          setSaving(false);
          return;
        }
      }
    }
    // Workout programmes from the cards.
    const workoutProgs = programmes.map(p => {
      const coded = tbComputeCodes(p.exercises.filter(e => e.name.trim()));
      return {
        name: p.name.trim() || 'Workout',
        kind: 'workout',
        placement: 'standalone',
        exercises: coded.map(e => ({
          code: e.code || null,
          name: e.name.trim(),
          sets: e.sets === '' ? null : Number(e.sets),
          reps: (e.reps || '').trim() || null,
          tempo: (e.tempo || '').trim() || null,
          rest_seconds: e.rest_seconds === '' ? null : Number(e.rest_seconds),
          notes: (e.notes || '').trim() || null,
          // Coach's prescribed starting load (null unless set) — prefills the
          // member's log screen and the trainer's PT Live guided session.
          target_weight: (String(e.target_weight ?? '')).trim() === '' ? null : Number(e.target_weight),
          // Cardio targets (null unless set) — mirror the log screen fields.
          t_time: (e.t_time || '') === '' ? null : Number(e.t_time),
          t_distance: (e.t_distance || '') === '' ? null : Number(e.t_distance),
          t_resistance: (e.t_resistance || '') === '' ? null : Number(e.t_resistance),
          t_calories: (e.t_calories || '') === '' ? null : Number(e.t_calories),
        })),
      };
    });

    // The blue "Stretching & Mobility" box → a single mobility programme.
    const mobEx = mobility
      .filter(m => (m.name || '').trim())
      .map(m => ({
        code: null,
        name: m.name.trim(),
        sets: null,
        reps: m.mode === 'split'
          ? [m.left ? `L ${m.left}` : '', m.right ? `R ${m.right}` : ''].filter(Boolean).join(' / ') || null
          : ((m.amount || '').trim() || null),
        tempo: null, rest_seconds: null, notes: null,
      }));
    const mobProgs = mobEx.length
      ? [{ name: 'Stretching & Mobility', kind: 'mobility', placement: mobInProgramme ? 'preworkout' : 'standalone', exercises: mobEx }]
      : [];

    const payload = {
      name: phaseName.trim(),
      mobility: [], // mobility now lives as a programme, not phase-level jsonb
      programmes: [...workoutProgs, ...mobProgs],
    };

    // Build-ahead-by-email: no account picker — hand it to the by-email RPC,
    // which attaches it now if the email already has an account, or parks it
    // until they sign up.
    if (emailMode) {
      const { data: d, error: e } = await supabase.rpc('admin_assign_phase_by_email', { p_email: emailAddr.trim(), payload });
      setSaving(false);
      if (e) { setError(e.message || 'Could not save phase.'); return; }
      const status = (d && d.status) || 'pending';
      setMsg(status === 'assigned'
        ? `${emailAddr.trim()} already has an account — phase attached now.`
        : `Saved. ${emailAddr.trim()} is now a prospect in your member list — search their email to view, edit or run PT Live. It all merges into their account when they sign up.`);
      resetForm();
      loadPending();
      // Refresh the member list so the new prospect is pickable straight away.
      supabase.rpc('admin_list_members').then(({ data }) => setMembers(data || []));
      return;
    }

    const { error: rpcErr } = editingPhaseId
      ? await supabase.rpc('admin_update_phase', { p_phase_id: editingPhaseId, payload })
      : await supabase.rpc('admin_create_phase', { target_member: target.id, payload });
    setSaving(false);
    if (rpcErr) { setError(rpcErr.message || 'Could not save phase.'); return; }

    // On a brand-new assignment, let the member know a programme is waiting.
    let pushNote = '';
    if (!editingPhaseId) {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://tb-app-production.up.railway.app/api';
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${apiUrl}/admin/push`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({
            target: 'member', memberId: target.id,
            title: 'Your coach added a new programme 🏋️',
            body: `“${payload.name}” is ready in your training — go take a look.`,
          }),
        });
        const d = await res.json().catch(() => ({}));
        pushNote = (res.ok && d.ok && d.sent > 0)
          ? ' · notification sent'
          : ' · (member hasn\u2019t enabled notifications)';
      } catch (_) { pushNote = ''; }
    }

    setMsg(editingPhaseId
      ? `Phase updated for ${target.name || target.email}.`
      : `Phase assigned to ${target.name || target.email}.${pushNote}`);
    resetForm();
    supabase.rpc('admin_get_member_training', { target_member: target.id })
      .then(({ data }) => setExisting(data || []));
  }

  async function deletePhase(ph) {
    if (!window.confirm(`Delete "${ph.name}" for this member? This removes its workouts and cannot be undone.`)) return;
    await supabase.rpc('admin_delete_phase', { p_phase_id: ph.id });
    if (editingPhaseId === ph.id) resetForm();
    supabase.rpc('admin_get_member_training', { target_member: target.id })
      .then(({ data }) => setExisting(data || []));
  }

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--grey-300)', background: '#fff', fontSize: '0.95rem' };
  const half = { ...inputStyle, padding: '8px 10px', fontSize: '0.9rem' };

  return (
    <div style={{ background: '#fff', borderRadius: 14, boxShadow: 'var(--shadow)', padding: 16 }}>
      <div style={{ fontWeight: 800, marginBottom: 10 }}>{editingPhaseId ? 'Edit a training phase' : 'Assign a training phase'}</div>

      {/* Member picker (existing account) */}
      {!emailMode && (
        <>
          <input style={inputStyle} value={target ? (target.name || target.email) : search}
            onChange={e => { setTarget(null); setSearch(e.target.value); }}
            placeholder="Search member by name or email" />
          {!target && search.trim() && (
            <div style={{ border: '1px solid var(--grey-200, #eee)', borderRadius: 8, marginTop: 6 }}>
              {filtered.length === 0 && <div style={{ padding: 10, color: 'var(--grey-500)', fontSize: '0.85rem' }}>No matches.</div>}
              {filtered.map(m => (
                <button key={m.id} type="button" onClick={() => { setTarget(m); setSearch(''); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', fontSize: '0.88rem', borderBottom: '1px solid var(--grey-100)' }}>
                  <span style={{ fontWeight: 700 }}>{m.name || '(no name)'}</span>
                  <span style={{ color: 'var(--grey-500)' }}> · {m.email}</span>
                  {m.is_prospect && (
                    <span style={{ marginLeft: 8, fontSize: '0.64rem', fontWeight: 800, color: '#8A4B00', background: '#FFE1C2', borderRadius: 999, padding: '2px 8px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      No account yet
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {target && (
            <div style={{ marginTop: 6, fontSize: '0.82rem', color: 'var(--green)' }}>
              Assigning to <strong>{target.name || target.email}</strong> ·{' '}
              <button type="button" onClick={() => setTarget(null)} style={{ color: 'var(--grey-500)', textDecoration: 'underline' }}>change</button>
            </div>
          )}
          {!editingPhaseId && (
            <button type="button" onClick={() => { setTarget(null); setSearch(''); setEmailMode(true); setError(''); setMsg(''); }}
              style={{ marginTop: 8, color: 'var(--green)', fontWeight: 700, fontSize: '0.8rem' }}>
              ✉️ Client has no account yet? Build for their email
            </button>
          )}
        </>
      )}

      {/* Build-ahead by email (no account yet) */}
      {emailMode && (
        <div style={{ background: '#F2FBF5', border: '1px solid var(--green-pale, #cdeed7)', borderRadius: 10, padding: 12 }}>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 6 }}>Build for a client without an account</div>
          <input style={inputStyle} type="email" value={emailAddr}
            onChange={e => setEmailAddr(e.target.value)}
            placeholder="client@email.com" />
          <div style={{ fontSize: '0.76rem', color: 'var(--grey-500)', marginTop: 6 }}>
            It’ll be waiting and attach automatically when they sign up with this email.
          </div>
          <button type="button" onClick={() => { setEmailMode(false); setEmailAddr(''); setError(''); setMsg(''); }}
            style={{ marginTop: 6, color: 'var(--grey-500)', textDecoration: 'underline', fontSize: '0.8rem' }}>
            ← Back to picking an existing member
          </button>
        </div>
      )}

      {/* Programmes waiting on a sign-up */}
      {!emailMode && pending.length > 0 && (
        <div style={{ marginTop: 12, background: '#FFF8F0', border: '1px solid #FFE1C2', borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#C24E00', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Waiting on sign-up</div>
          {pending.map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '0.84rem' }}>
              <span><strong>{p.name || 'Phase'}</strong> <span style={{ color: 'var(--grey-500)' }}>· {p.email}</span></span>
              <button type="button" onClick={() => deletePending(p)} style={{ color: 'var(--danger)', fontSize: '0.74rem', fontWeight: 600, flexShrink: 0 }}>Cancel</button>
            </div>
          ))}
        </div>
      )}

      {/* Existing phases for this member */}
      {target && existing.length > 0 && (
        <div style={{ marginTop: 12, background: 'var(--grey-50)', borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--grey-500)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>Current phases</div>
          {existing.map(ph => (
            <div key={ph.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: '0.86rem', borderTop: '1px solid var(--grey-100)' }}>
              <span style={{ minWidth: 0 }}><strong>{ph.name}</strong> <span style={{ color: 'var(--grey-500)' }}>· {(ph.programmes || []).map(p => p.name).join(', ') || 'no workouts'}</span></span>
              <span style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                <button type="button" onClick={() => addWorkoutToPhase(ph)}
                  style={{ color: '#fff', background: 'var(--green)', fontSize: '0.74rem', fontWeight: 800, padding: '5px 10px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                  + Add workout
                </button>
                <button type="button" onClick={() => editPhase(ph)} style={{ color: 'var(--grey-700)', fontSize: '0.75rem', fontWeight: 700 }}>Edit</button>
                <button type="button" onClick={() => deletePhase(ph)} style={{ color: 'var(--danger)', fontSize: '0.75rem', fontWeight: 600 }}>Delete</button>
              </span>
            </div>
          ))}
          <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', marginTop: 8 }}>
            Want another workout (1B, 1C…) in a block? Tap <strong style={{ color: 'var(--green)' }}>+ Add workout</strong> on that phase — don't build a new one below, or it becomes a separate phase.
          </div>
        </div>
      )}

      {/* Exercise library (shared dropdown source). Add a custom one and it
          appears in every member's and trainer's dropdown. */}
      {target && (
        <div style={{ marginTop: 12 }}>
          <button type="button" onClick={() => setLibOpen(o => !o)}
            style={{ color: 'var(--green)', fontWeight: 700, fontSize: '0.82rem' }}>
            {libOpen ? '− Hide exercise library' : '+ Add a new exercise to the library'}
          </button>
          {libOpen && (
            <div style={{ background: 'var(--grey-50)', borderRadius: 10, padding: 12, marginTop: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 6 }}>
                <input style={half} value={newExName} onChange={e => setNewExName(e.target.value)} placeholder="New exercise name" />
                <select style={half} value={newExPart} onChange={e => setNewExPart(e.target.value)}>
                  {TB_BODY_PART_ORDER.map(bp => <option key={bp} value={bp}>{bp}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6, marginTop: 6 }}>
                <select style={half} value={newExLoad} onChange={e => setNewExLoad(e.target.value)}>
                  <option value="weighted">Weighted (barbell, dumbbell, machine…)</option>
                  <option value="bodyweight">Bodyweight (dips, chins, press-ups — assist/belt allowed)</option>
                </select>
              </div>
              <div style={{ fontSize: '0.74rem', color: 'var(--grey-500)', marginTop: 6 }}>
                Bodyweight moves count toward training volume using the member's latest scan weight (± any belt or assist they log).
              </div>
              {libErr && <div style={{ color: 'var(--danger)', fontSize: '0.78rem', marginTop: 6 }}>{libErr}</div>}
              <button type="button" className="btn btn-secondary" style={{ marginTop: 8, fontSize: '0.82rem' }} onClick={addToLibrary} disabled={libSaving}>
                {libSaving ? 'Adding…' : 'Add to library'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Make the two paths unmistakable: are we changing an existing phase,
          or creating a brand-new one? */}
      {editingPhaseId ? (
        <div style={{ marginTop: 14, background: '#F2FBF5', border: '1px solid var(--green-pale, #cdeed7)', borderRadius: 10, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: '0.82rem', color: 'var(--grey-700)' }}>
            ✏️ Editing <strong>{phaseName || 'this phase'}</strong> — your changes save <strong>into this phase</strong> (no new phase is created).
          </div>
          <button type="button" onClick={resetForm}
            style={{ color: 'var(--grey-500)', fontSize: '0.75rem', fontWeight: 700, textDecoration: 'underline', flexShrink: 0 }}>
            Start a new phase instead
          </button>
        </div>
      ) : (target && existing.length > 0 && (
        <div style={{ marginTop: 14, background: '#FFF8F0', border: '1px solid #FFE1C2', borderRadius: 10, padding: '10px 12px', fontSize: '0.82rem', color: '#8A4B00' }}>
          ➕ This creates a <strong>brand-new phase</strong>. To add a workout to a phase they already have, use <strong>+ Add workout</strong> above instead.
        </div>
      ))}

      {/* New / edit phase builder */}
      <div style={{ marginTop: 14 }}>
        <input style={inputStyle} value={phaseName} onChange={e => setPhaseName(e.target.value)} placeholder="Phase name (e.g. Phase 1)" />
      </div>

      <div style={{ marginTop: 12 }}>
        <TbMobilityEditor items={mobility} onChange={setMobility}
          inProgramme={mobInProgramme} onInProgrammeChange={setMobInProgramme} />
      </div>

      {programmes.map((p, pi) => {
        const isMob = p.kind === 'mobility';
        const coded = isMob ? p.exercises : tbComputeCodes(p.exercises);
        return (
        <div key={pi} style={{ background: isMob ? '#FFF3EC' : 'var(--grey-50)', border: isMob ? '1px solid #FFD2B8' : 'none', borderRadius: 10, padding: 12, marginTop: 12 }}>
          {isMob && (
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#C24E00', marginBottom: 8 }}>🤸 Stretching &amp; Mobility</div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <input style={{ ...inputStyle, fontWeight: 700 }} value={p.name}
              onChange={e => updateProg(pi, 'name', e.target.value)} placeholder={isMob ? 'Name (optional, e.g. Hips & T-spine)' : 'Workout (e.g. 1A)'} />
            {programmes.length > 1 && (
              <button type="button" onClick={() => removeProg(pi)} style={{ color: 'var(--danger)', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>Remove</button>
            )}
          </div>

          {/* Mobility: one choice — part of the programme, or its own session */}
          {isMob && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10, cursor: 'pointer', fontSize: '0.82rem', color: 'var(--grey-700)' }}>
              <input type="checkbox" checked={p.placement === 'preworkout'}
                onChange={ev => updateProg(pi, 'placement', ev.target.checked ? 'preworkout' : 'standalone')}
                style={{ marginTop: 2, flexShrink: 0 }} />
              <span>
                <strong>Add to the programme</strong> — shows before every workout in this phase.
                <span style={{ color: 'var(--grey-500)' }}> Leave unticked for its own standalone session.</span>
              </span>
            </label>
          )}

          {p.exercises.map((e, ei) => {
            const rowCode = coded[ei] ? coded[ei].code : 'A1';
            const opts = ei === 0 ? ['A1', 'A1'] : tbNextCodeOptions((coded[ei - 1] || {}).code);
            return (
            <div key={ei} style={{ background: '#fff', border: `1px solid ${isMob ? '#FFD2B8' : 'var(--grey-200, #eee)'}`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMob ? '1fr 22px' : '96px 1fr 22px', gap: 6, marginBottom: 6 }}>
                {!isMob && (ei === 0 ? (
                  <div style={{ ...half, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--grey-700)', background: 'var(--grey-50)' }} title="First exercise in this workout">{rowCode}</div>
                ) : (
                  <select style={{ ...half, padding: '8px 6px', fontWeight: 700, cursor: 'pointer', borderColor: (e.rel === 'same' || e.rel === 'new') ? undefined : 'var(--danger)', color: (e.rel === 'same' || e.rel === 'new') ? undefined : 'var(--danger)' }}
                    value={e.rel === 'new' ? 'new' : e.rel === 'same' ? 'same' : ''} onChange={ev => updateEx(pi, ei, 'rel', ev.target.value)}>
                    <option value="" disabled>Group?</option>
                    <option value="same">{opts[0]} · same</option>
                    <option value="new">{opts[1]} · new</option>
                  </select>
                ))}
                {e._custom ? (
                  <input style={half} value={e.name} autoFocus
                    onChange={ev => updateEx(pi, ei, 'name', ev.target.value)} placeholder={isMob ? 'Custom stretch name' : 'Custom exercise name'} />
                ) : (
                  <button type="button" onClick={() => setPickerFor({ pi, ei, isMob })}
                    style={{ ...half, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, color: nameSet.has(e.name) ? undefined : 'var(--grey-500)' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {nameSet.has(e.name) ? e.name : (catLoaded ? (isMob ? 'Choose stretch…' : 'Choose exercise…') : 'Loading…')}
                    </span>
                    <span style={{ color: 'var(--grey-400)', fontSize: '0.82rem', flexShrink: 0 }}>🔍</span>
                  </button>
                )}
                <button type="button" onClick={() => removeEx(pi, ei)} disabled={p.exercises.length <= 1}
                  style={{ color: p.exercises.length <= 1 ? 'var(--grey-300)' : 'var(--danger)', fontSize: '1.1rem' }}>×</button>
              </div>
              {e._custom && (
                <button type="button" onClick={() => { updateEx(pi, ei, '_custom', false); updateEx(pi, ei, 'name', ''); }}
                  style={{ color: 'var(--grey-500)', fontSize: '0.72rem', textDecoration: 'underline', marginBottom: 6 }}>
                  ↩ Pick from list instead
                </button>
              )}
              {isMob ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 6 }}>
                  <input style={half} type="number" inputMode="numeric" value={e.sets} onChange={ev => updateEx(pi, ei, 'sets', ev.target.value)} placeholder="Rounds" />
                  <input style={half} value={e.reps} onChange={ev => updateEx(pi, ei, 'reps', ev.target.value)} placeholder="Hold / reps (e.g. 45s)" />
                </div>
              ) : (partByName[e.name] === 'Cardio') ? (
                // Cardio: efforts + the same target boxes the member logs.
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <input style={{ ...half, gridColumn: '1 / -1' }} type="number" inputMode="numeric" value={e.sets} onChange={ev => updateEx(pi, ei, 'sets', ev.target.value)} placeholder="Efforts (rounds)" />
                  <input style={half} type="number" inputMode="decimal" value={e.t_time} onChange={ev => updateEx(pi, ei, 't_time', ev.target.value)} placeholder="Time (min)" />
                  <input style={half} type="number" inputMode="decimal" value={e.t_distance} onChange={ev => updateEx(pi, ei, 't_distance', ev.target.value)} placeholder="Distance" />
                  <input style={half} type="number" inputMode="decimal" value={e.t_resistance} onChange={ev => updateEx(pi, ei, 't_resistance', ev.target.value)} placeholder="Resistance" />
                  <input style={half} type="number" inputMode="numeric" value={e.t_calories} onChange={ev => updateEx(pi, ei, 't_calories', ev.target.value)} placeholder="Calories" />
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 6 }}>
                  <input style={half} type="number" inputMode="numeric" value={e.sets} onChange={ev => updateEx(pi, ei, 'sets', ev.target.value)} placeholder="Sets" />
                  <input style={half} value={e.reps} onChange={ev => updateEx(pi, ei, 'reps', ev.target.value)} placeholder="Reps" />
                  <input style={half} value={e.tempo} onChange={ev => updateEx(pi, ei, 'tempo', ev.target.value)} placeholder="Tempo" />
                  <input style={half} type="number" inputMode="numeric" value={e.rest_seconds} onChange={ev => updateEx(pi, ei, 'rest_seconds', ev.target.value)} placeholder="Rest s" />
                  <input style={half} type="number" inputMode="decimal" value={e.target_weight} onChange={ev => updateEx(pi, ei, 'target_weight', ev.target.value)} placeholder="Target kg" />
                </div>
              )}
              <input style={{ ...half, marginTop: 6 }} value={e.notes} onChange={ev => updateEx(pi, ei, 'notes', ev.target.value)} placeholder="Notes (optional)" />
            </div>
            );
          })}
          <button type="button" onClick={() => addEx(pi)} style={{ color: isMob ? '#FF6200' : 'var(--green)', fontWeight: 700, fontSize: '0.82rem', marginTop: 2 }}>
            {isMob ? '+ Add stretch' : '+ Add exercise'}
          </button>
        </div>
        );
      })}

      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={addProg} style={{ color: 'var(--green)', fontWeight: 700, fontSize: '0.86rem' }}>+ Add workout</button>
      </div>

      <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', marginTop: 8 }}>
        Codes set themselves: pick <strong>same</strong> to superset with the exercise above (A1 &amp; A2), or <strong>new</strong> to start a fresh group (B1). Tempo like 4010. Rest in seconds. Mobility blocks can be their own session or a warm-up before every workout.
      </div>

      {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: 10 }}>{error}</div>}
      {msg && <div style={{ color: 'var(--green)', fontSize: '0.85rem', marginTop: 10 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        {editingPhaseId && (
          <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={resetForm} disabled={saving}>
            Cancel edit
          </button>
        )}
        <button type="button" className="btn btn-primary" style={{ flex: 2 }} onClick={assign} disabled={saving}>
          {saving ? 'Saving…' : (editingPhaseId ? 'Save changes to this phase' : (emailMode ? 'Save for email' : 'Create new phase'))}
        </button>
      </div>

      {pickerFor && (
        <TbExercisePicker
          orderedParts={orderedParts}
          grouped={grouped}
          isMob={pickerFor.isMob}
          value={(() => {
            const pr = programmes[pickerFor.pi];
            const ex = pr && pr.exercises[pickerFor.ei];
            return ex ? ex.name : '';
          })()}
          onClose={() => setPickerFor(null)}
          onPick={(value) => { const pf = pickerFor; setPickerFor(null); onSelectName(pf.pi, pf.ei, value); }}
        />
      )}
    </div>
  );
}

// ── PT Live: a trainer logs a session straight onto a client's account ──
// Pick a client, pick one of their workouts, fill in weights/reps live, and
// save it to their training history via admin_log_training_session.
// ── Guided PT Live panel ──────────────────────────────────────────────
// The coach-run version of the member's guided workout: one set at a time,
// pre-filled reps/weight from last time (or the coach's target), big +/-
// steppers, a rest countdown, and mid-session swap/skip. Writes straight into
// the same `rows` state the old-school list uses, so the two stay in sync and
// save() behaves identically.
function AdminGuidedSession({
  rows, setField, lastLogs, isBodyweight, roundW,
  bodyWeight, setBodyWeight, scanWeight,
  grouped, orderedParts, onSwap, onSkip,
  notes, setNotes, onFinish, saving, error, onBackToPlan,
}) {
  // One step per set, in programme order (no superset interleave — keep it
  // predictable for the coach).
  const steps = useMemo(() => {
    const out = [];
    rows.forEach((r, exIdx) => {
      (r.sets || []).forEach((_, setIdx) => {
        out.push({ exIdx, setIdx, roundCount: (r.sets || []).length });
      });
    });
    return out;
  }, [rows]);
  const rowsRef = useRef(rows); rowsRef.current = rows;

  const [pos, setPos] = useState(0);
  const [rest, setRest] = useState(null);
  const [restTotal, setRestTotal] = useState(0);
  const [swapOpen, setSwapOpen] = useState(false);

  function lastStructured(name) {
    if (!name) return null;
    const l = lastLogs[name.trim().toLowerCase()];
    if (!l || !Array.isArray(l.sets) || l.sets.length === 0) return null;
    return l;
  }

  // Preload the current set once — same set index from last time, else the
  // coach's target reps. Weight is left blank for bodyweight moves (the box is
  // a signed +belt/−assist extra).
  useEffect(() => {
    if (pos < 0 || pos >= steps.length) return;
    const st = steps[pos];
    const r = rowsRef.current[st.exIdx];
    if (!r) return;
    const s = r.sets[st.setIdx];
    if (!s) return;
    const hasW = s.weight !== '' && s.weight != null;
    const hasR = s.reps !== '' && s.reps != null;
    if (hasW || hasR) return; // already has input
    const last = lastStructured(r.name);
    const li = last ? last.sets[st.setIdx] : null;
    let w = null, reps = null;
    if (li) { w = li.weight; reps = li.reps; }
    else { w = (r.target_weight == null || r.target_weight === '') ? null : r.target_weight; reps = r.reps_target; }
    const bw = isBodyweight(r.name);
    if (!bw && w != null && w !== '' && /^-?\d+(\.\d+)?$/.test(String(w))) setField(st.exIdx, st.setIdx, 'weight', String(w));
    if (reps != null && reps !== '' && /^\d+$/.test(String(reps))) setField(st.exIdx, st.setIdx, 'reps', String(reps));
  }, [pos]); // eslint-disable-line

  // Rest countdown — a guide only; never auto-advances. Buzzes once at zero.
  useEffect(() => {
    if (rest == null) return undefined;
    if (rest <= 0) return undefined;
    const t = setTimeout(() => setRest(r => {
      if (r == null) return null;
      const nx = r - 1;
      if (nx <= 0) { try { if (navigator.vibrate) navigator.vibrate(220); } catch (_) {} return 0; }
      return nx;
    }), 1000);
    return () => clearTimeout(t);
  }, [rest]);

  function logCurrent() {
    const st = steps[pos];
    const r = rowsRef.current[st.exIdx];
    const secs = Number(r && r.rest);
    const isLast = pos >= steps.length - 1;
    setPos(p => p + 1);
    if (!isLast && secs > 0) { setRestTotal(secs); setRest(secs); }
    else setRest(null);
  }
  function dismissRest() { setRest(null); }
  function skipExercise() {
    const curEx = steps[pos] ? steps[pos].exIdx : null;
    if (curEx != null && onSkip) onSkip(curEx);
    let p = pos + 1;
    while (p < steps.length && steps[p].exIdx === curEx) p += 1;
    setRest(null);
    setPos(p);
  }
  function goBack() { setRest(null); setPos(p => Math.max(0, p - 1)); }

  const label = { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', color: 'var(--grey-500)', textTransform: 'uppercase', marginBottom: 4 };
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--grey-300)', background: '#fff', fontSize: '0.95rem', boxSizing: 'border-box' };

  const planLink = (
    <div style={{ textAlign: 'center', marginTop: 14 }}>
      <button type="button" onClick={onBackToPlan} style={{ color: 'var(--grey-500)', fontSize: '0.78rem', fontWeight: 600, textDecoration: 'underline' }}>
        See the whole plan
      </button>
    </div>
  );

  // ── Empty ───────────────────────────────────────────────────────────
  if (steps.length === 0) {
    return (
      <div style={{ color: 'var(--grey-500)', fontSize: '0.9rem', padding: '8px 0 16px' }}>
        This workout has no exercises yet.
      </div>
    );
  }

  // ── Finish screen ───────────────────────────────────────────────────
  if (pos >= steps.length) {
    let done = 0;
    rows.forEach(r => (r.sets || []).forEach(s => { if ((s.weight !== '' && s.weight != null) || (s.reps !== '' && s.reps != null)) done += 1; }));
    return (
      <>
        <div style={{ textAlign: 'center', padding: '6px 0 2px' }}>
          <div style={{ fontSize: '2.4rem' }}>🎉</div>
          <div style={{ fontWeight: 800, fontSize: '1.15rem' }}>Workout complete</div>
          <div style={{ color: 'var(--grey-500)', fontSize: '0.9rem', marginTop: 2 }}>{done} set{done === 1 ? '' : 's'} logged</div>
        </div>
        <div style={{ marginTop: 14 }}>
          <textarea style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} value={notes}
            onChange={e => setNotes(e.target.value)} placeholder="Session notes (optional)" />
        </div>
        {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: 10 }}>{error}</div>}
        <button type="button" className="btn btn-primary btn-full" style={{ marginTop: 12 }} onClick={onFinish} disabled={saving}>
          {saving ? 'Saving…' : 'Finish & log session'}
        </button>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 12 }}>
          <button type="button" onClick={() => setPos(steps.length - 1)} style={{ color: 'var(--grey-500)', fontSize: '0.8rem', fontWeight: 600 }}>← Back to last set</button>
          <button type="button" onClick={onBackToPlan} style={{ color: 'var(--grey-500)', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'underline' }}>See whole plan</button>
        </div>
      </>
    );
  }

  // ── A single set ────────────────────────────────────────────────────
  const st = steps[pos];
  const r = rows[st.exIdx];
  const s = r.sets[st.setIdx] || { weight: '', reps: '' };
  const bw = isBodyweight(r.name);
  const bwNum = bodyWeight === '' ? null : Number(bodyWeight);
  const delta = (s.weight === '' || s.weight == null) ? 0 : Number(s.weight);
  const effective = (bw && bwNum != null && !Number.isNaN(bwNum)) ? roundW(Math.max(0, bwNum + delta)) : null;
  const last = lastStructured(r.name);
  const lastSet = last ? last.sets[st.setIdx] : null;
  const progressPct = Math.round((pos / steps.length) * 100);

  const bigNum = { flex: 1, minWidth: 0, width: '100%', textAlign: 'center', fontSize: '1.8rem', fontWeight: 800, padding: '12px 4px', borderRadius: 12, border: '1px solid var(--grey-300)', fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box' };
  const stepBtn = { width: 56, height: 56, borderRadius: 12, border: '1px solid var(--grey-300)', background: 'var(--grey-50)', fontSize: '1.6rem', fontWeight: 800, color: 'var(--grey-700)', lineHeight: 1, flexShrink: 0 };
  const fineBtn = { padding: '5px 14px', borderRadius: 999, border: '1px solid var(--grey-300)', background: '#fff', fontSize: '0.8rem', fontWeight: 700, color: 'var(--grey-500)', lineHeight: 1 };

  function adjust(field, d) {
    const raw = s[field];
    let next;
    if (field === 'weight') next = roundW(Math.max(bw ? -500 : 0, (raw === '' || raw == null ? 0 : Number(raw)) + d));
    else next = Math.max(0, (raw === '' || raw == null ? 0 : Number(raw)) + d);
    setField(st.exIdx, st.setIdx, field, String(next));
  }

  let restBar = null;
  if (rest != null) {
    const mm = Math.floor(rest / 60), ss = rest % 60;
    const ready = rest <= 0;
    const barPct = ready ? 100 : (restTotal > 0 ? Math.min(100, Math.round((rest / restTotal) * 100)) : 0);
    restBar = (
      <div style={{ border: `1px solid ${ready ? '#FFC2A0' : 'var(--grey-300)'}`, background: ready ? '#FFF3EC' : 'var(--grey-50)', borderRadius: 12, padding: '10px 12px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
            <span style={{ ...label, color: ready ? '#FF6200' : 'var(--green)', marginBottom: 0 }}>{ready ? "Rest's up" : 'Rest'}</span>
            <span style={{ fontSize: '1.3rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: ready ? '#FF6200' : undefined, lineHeight: 1 }}>{mm}:{String(ss).padStart(2, '0')}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button type="button" style={fineBtn} onClick={() => setRest(rr => Math.max(0, (rr || 0) - 15))}>−15</button>
            <button type="button" style={fineBtn} onClick={() => setRest(rr => { const nx = (rr || 0) + 15; setRestTotal(t => Math.max(t, nx)); return nx; })}>+15</button>
            <button type="button" style={{ ...fineBtn, color: ready ? '#FF6200' : 'var(--grey-700)', borderColor: ready ? '#FFC2A0' : 'var(--grey-300)' }} onClick={dismissRest}>Dismiss</button>
          </div>
        </div>
        <div style={{ height: 8, borderRadius: 999, background: 'var(--grey-100)', overflow: 'hidden', marginTop: 8 }}>
          <div style={{ height: '100%', width: `${barPct}%`, background: ready ? '#FF6200' : 'var(--green)', borderRadius: 999, transition: 'width 1s linear' }} />
        </div>
      </div>
    );
  }

  return (
    <>
      {restBar}

      {/* progress */}
      <div style={{ height: 6, borderRadius: 999, background: 'var(--grey-100)', marginBottom: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${progressPct}%`, background: 'var(--green)', borderRadius: 999, transition: 'width 0.25s' }} />
      </div>
      <div style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--grey-500)', fontWeight: 700, letterSpacing: '0.04em', marginBottom: 12 }}>
        SET {pos + 1} OF {steps.length}
      </div>

      {/* exercise */}
      <div style={{ textAlign: 'center', marginBottom: 6 }}>
        <div style={{ fontWeight: 800, fontSize: '1.3rem' }}>
          {r.code ? <span style={{ color: 'var(--grey-500)', marginRight: 8 }}>{r.code}</span> : null}
          {r.name}
        </div>
        <div style={{ color: 'var(--grey-500)', fontSize: '0.85rem', marginTop: 2 }}>
          Set {st.setIdx + 1} of {st.roundCount}
          {r.reps_target ? ` · target ${r.reps_target} reps` : ''}
          {r.tempo ? ` · tempo ${r.tempo}` : ''}
        </div>
        {lastSet && (
          <div style={{ display: 'inline-block', marginTop: 8, fontSize: '0.75rem', color: 'var(--grey-700)', background: 'var(--green-pale)', borderRadius: 6, padding: '4px 10px' }}>
            <span style={{ fontWeight: 700, color: 'var(--green)' }}>Last time:</span> {lastSet.weight != null ? `${lastSet.weight}kg` : 'BW'} × {lastSet.reps != null ? lastSet.reps : '–'}
          </div>
        )}
      </div>

      {/* Bodyweight banner */}
      {bw && (
        <div style={{ background: bwNum == null ? '#FFF8F0' : 'var(--green-pale)', border: `1px solid ${bwNum == null ? '#FFD2B8' : 'var(--green-pale)'}`, borderRadius: 10, padding: '10px 12px', margin: '6px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ ...label, marginBottom: 0, color: bwNum == null ? '#C24E00' : 'var(--green)' }}>Client bodyweight</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="number" inputMode="decimal" value={bodyWeight} onChange={e => setBodyWeight(e.target.value)} placeholder="kg"
                style={{ width: 84, textAlign: 'center', fontWeight: 800, fontSize: '1rem', padding: '6px 8px', borderRadius: 8, border: '1px solid var(--grey-300)' }} />
              <span style={{ fontWeight: 700, color: 'var(--grey-500)' }}>kg</span>
            </div>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', marginTop: 4 }}>
            {scanWeight != null ? 'From their last InBody scan — tweak if it’s changed.' : 'No scan on file — pop their current bodyweight in so these sets count.'}
          </div>
        </div>
      )}

      {/* inputs */}
      <div style={{ margin: '16px 0' }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ ...label, textAlign: 'center', marginBottom: 6 }}>{bw ? '+ Belt / − Assist (kg)' : 'Weight (kg)'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="button" style={stepBtn} onClick={() => adjust('weight', -2.5)}>−</button>
            <input type="number" inputMode="decimal" style={bigNum} value={s.weight}
              onChange={e => setField(st.exIdx, st.setIdx, 'weight', e.target.value)} placeholder={bw ? '0' : 'BW'} />
            <button type="button" style={stepBtn} onClick={() => adjust('weight', 2.5)}>+</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
            <button type="button" style={fineBtn} onClick={() => adjust('weight', -0.5)}>−0.5</button>
            <button type="button" style={fineBtn} onClick={() => adjust('weight', 0.5)}>+0.5</button>
          </div>
          {bw && (
            <div style={{ textAlign: 'center', fontSize: '0.74rem', color: 'var(--grey-500)', marginTop: 8 }}>
              {delta === 0 ? 'Bodyweight only' : delta > 0 ? `Bodyweight + ${roundW(delta)}kg` : `Assisted ${roundW(-delta)}kg`}
              {effective != null && <> · counts as <strong style={{ color: 'var(--green)' }}>{effective}kg</strong></>}
            </div>
          )}
        </div>
        <div>
          <div style={{ ...label, textAlign: 'center', marginBottom: 6 }}>Reps</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="button" style={stepBtn} onClick={() => adjust('reps', -1)}>−</button>
            <input type="number" inputMode="numeric" style={bigNum} value={s.reps}
              onChange={e => setField(st.exIdx, st.setIdx, 'reps', e.target.value)} placeholder="–" />
            <button type="button" style={stepBtn} onClick={() => adjust('reps', 1)}>+</button>
          </div>
        </div>
      </div>

      <button type="button" className="btn btn-primary btn-full" onClick={logCurrent}>
        {pos === steps.length - 1 ? 'Log set & finish →' : (Number(r.rest) > 0 ? 'Log set & rest →' : 'Log set →')}
      </button>

      {/* Mid-session changes — swap if a machine's taken, skip if it hurts. */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 12 }}>
        <button type="button" onClick={() => setSwapOpen(true)} style={{ ...fineBtn, color: 'var(--grey-700)' }}>⇄ Swap exercise</button>
        <button type="button" onClick={skipExercise} style={{ ...fineBtn, color: 'var(--grey-700)' }}>Skip exercise →</button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 12 }}>
        <button type="button" onClick={goBack} disabled={pos <= 0}
          style={{ color: pos <= 0 ? 'var(--grey-300)' : 'var(--grey-500)', fontSize: '0.82rem', fontWeight: 600 }}>← Back</button>
      </div>
      {planLink}

      {swapOpen && (
        <TbExercisePicker orderedParts={orderedParts} grouped={grouped}
          value={r.name} isMob={false}
          onClose={() => setSwapOpen(false)}
          onPick={(picked) => {
            setSwapOpen(false);
            if (!picked) return;
            if (picked === '__custom__' || picked.startsWith('__custom__::')) {
              const part = picked.includes('::') ? picked.split('::')[1] : '';
              const nm = (typeof window !== 'undefined' && window.prompt) ? window.prompt(part ? `Swap to (${part}):` : 'Swap to exercise:') : '';
              if (nm && nm.trim() && onSwap) onSwap(st.exIdx, nm.trim());
              return;
            }
            if (onSwap) onSwap(st.exIdx, picked);
          }} />
      )}
    </>
  );
}

function AdminLiveSession() {
  const [members, setMembers] = useState([]);
  const [search, setSearch]   = useState('');
  const [target, setTarget]   = useState(null); // { id, name, email }
  const [tree, setTree]       = useState([]);   // the client's phases
  const [lastLogs, setLastLogs] = useState({});  // keyed lower(name) → last sets
  const [selPhase, setSelPhase] = useState(null); // phase object
  const [selProg, setSelProg]   = useState(null); // programme object
  const [rows, setRows]       = useState([]);     // [{ exercise_id, code, name, tempo, rest, reps_target, sets:[{weight,reps}] }]
  const [notes, setNotes]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const [error, setError]     = useState('');
  const [catLoadMap, setCatLoadMap] = useState({}); // lower(name) → load_type
  const [scanWeight, setScanWeight] = useState(null);
  const [bodyWeight, setBodyWeight] = useState('');  // client bodyweight used this session
  const [mode, setMode]       = useState('guided');  // 'guided' | 'classic' (old-school)
  const [guidedStarted, setGuidedStarted] = useState(false); // show the plan overview first
  const [skippedIdx, setSkippedIdx] = useState(() => new Set()); // exercises skipped this session
  const [catalogue, setCatalogue] = useState([]);    // full exercise library (for swaps)

  const roundW = (n) => Math.round(Number(n) * 10) / 10;
  function isBodyweight(name) {
    return (catLoadMap[(name || '').trim().toLowerCase()] || '') === 'bodyweight';
  }

  useEffect(() => {
    supabase.rpc('admin_list_members').then(({ data }) => setMembers(data || []));
    supabase.rpc('get_exercise_catalogue').then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      const m = {};
      list.forEach(e => { if (e && e.name) m[e.name.trim().toLowerCase()] = e.load_type || 'weighted'; });
      setCatLoadMap(m);
      setCatalogue(list);
    }).catch(() => {});
  }, []);

  // ── Keep the screen awake + survive the screen going off ──────────────
  // Two things stop a live PT session needing a reload when the phone's
  // screen turns off: (1) a Wake Lock holds the screen on while a workout is
  // open, and (2) we stash the in-progress session in local storage so if the
  // browser does drop the page, it comes straight back where we left off.
  const clearDraft = () => { try { window.localStorage.removeItem('tb_ptlive_draft'); } catch (_) {} };

  const wakeRef = useRef(null);
  useEffect(() => {
    if (!selProg) return undefined;
    let released = false;
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator && navigator.wakeLock) {
          wakeRef.current = await navigator.wakeLock.request('screen');
        }
      } catch (_) {}
    };
    const onVis = () => { if (document.visibilityState === 'visible' && !released) acquire(); };
    acquire();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVis);
      try { if (wakeRef.current) { wakeRef.current.release(); wakeRef.current = null; } } catch (_) {}
    };
  }, [selProg]);

  // Restore a dropped session once, on mount.
  const hydratedRef = useRef(false);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('tb_ptlive_draft');
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.target && d.selProg && Array.isArray(d.rows)) {
          setTarget(d.target);
          setSelPhase(d.selPhase || null);
          setSelProg(d.selProg);
          setRows(d.rows);
          setNotes(d.notes || '');
          setMode(d.mode || 'guided');
          setGuidedStarted(!!d.guidedStarted);
          setSkippedIdx(new Set(Array.isArray(d.skipped) ? d.skipped : []));
          setBodyWeight(d.bodyWeight || '');
          if (d.scanWeight != null) setScanWeight(d.scanWeight);
          supabase.rpc('admin_get_member_training', { target_member: d.target.id }).then(({ data }) => setTree(data || []));
          supabase.rpc('admin_get_member_last_logs', { target_member: d.target.id }).then(({ data }) => setLastLogs(data || {}));
          setMsg('Picked up your session where you left off.');
        }
      }
    } catch (_) {}
    hydratedRef.current = true;
  }, []);

  // Save the in-progress session whenever anything in it changes.
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (!target || !selProg) return;
    try {
      window.localStorage.setItem('tb_ptlive_draft', JSON.stringify({
        target, selPhase, selProg, rows, notes, mode, guidedStarted,
        skipped: Array.from(skippedIdx), bodyWeight, scanWeight, savedAt: Date.now(),
      }));
    } catch (_) {}
  }, [target, selPhase, selProg, rows, notes, mode, guidedStarted, skippedIdx, bodyWeight, scanWeight]);

  // Grouped exercise library for the mid-session swap picker.
  const grouped = useMemo(() => {
    const g = {};
    for (const ex of catalogue) { const part = ex.body_part || 'Other'; (g[part] = g[part] || []).push(ex); }
    return g;
  }, [catalogue]);
  const orderedParts = useMemo(() => {
    const parts = Object.keys(grouped);
    return parts.sort((a, b) => {
      const ia = TB_BODY_PART_ORDER.indexOf(a), ib = TB_BODY_PART_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [grouped]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return members.filter(m =>
      (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
    ).slice(0, 12);
  }, [members, search]);

  function pickMember(m) {
    clearDraft();
    setTarget(m); setSearch(''); setSelPhase(null); setSelProg(null); setRows([]); setMsg(''); setError('');
    setScanWeight(null); setBodyWeight(''); resetGuided();
    supabase.rpc('admin_get_member_training', { target_member: m.id }).then(({ data }) => setTree(data || []));
    supabase.rpc('admin_get_member_last_logs', { target_member: m.id }).then(({ data }) => setLastLogs(data || {}));
    supabase.rpc('admin_get_member_inbody_scans', { target_member: m.id }).then(({ data }) => {
      const scans = Array.isArray(data) ? data : [];
      const w = scans.length && scans[0].weight_kg != null ? Number(scans[0].weight_kg) : null;
      if (w != null && !Number.isNaN(w)) { setScanWeight(w); setBodyWeight(String(w)); }
    }).catch(() => {});
  }

  function lastFor(name) {
    if (!name) return null;
    const l = lastLogs[name.trim().toLowerCase()];
    if (!l || !Array.isArray(l.sets) || l.sets.length === 0) return null;
    return l.sets.map(s => `${s.weight != null ? s.weight + 'kg' : 'BW'}×${s.reps != null ? s.reps : '–'}`).join(', ');
  }

  function openProgramme(ph, pr) {
    setSelPhase(ph); setSelProg(pr); setMsg(''); setError('');
    const built = (pr.exercises || []).map(e => {
      const n = Math.max(1, Math.min(12, Number(e.sets) || 3));
      return {
        exercise_id: e.id || null,
        code: e.code || '',
        name: e.name || '',
        tempo: e.tempo || '',
        rest: e.rest_seconds || null,
        reps_target: e.reps || '',
        target_weight: e.target_weight == null ? '' : String(e.target_weight),
        sets: Array.from({ length: n }, () => ({ weight: '', reps: '' })),
      };
    });
    setRows(built);
    setNotes('');
    resetGuided();
  }

  function setField(ri, si, field, value) {
    setRows(prev => prev.map((r, i) => i !== ri ? r : {
      ...r, sets: r.sets.map((s, j) => j !== si ? s : { ...s, [field]: value }),
    }));
  }
  function addSet(ri) {
    setRows(prev => prev.map((r, i) => i !== ri ? r : { ...r, sets: [...r.sets, { weight: '', reps: '' }] }));
  }
  function removeSet(ri, si) {
    setRows(prev => prev.map((r, i) => i !== ri ? r : { ...r, sets: r.sets.filter((_, j) => j !== si) }));
  }

  // Guided mode: swap an exercise for THIS SESSION only (the client's saved
  // programme is untouched). We change the name and drop the library id so it
  // logs cleanly by name, and un-skip it if it had been skipped.
  function onGuidedSwap(exIdx, name) {
    setRows(prev => prev.map((r, i) => i !== exIdx ? r : { ...r, name, exercise_id: null }));
    setSkippedIdx(prev => { const n = new Set(prev); n.delete(exIdx); return n; });
  }
  // Skip an exercise — it won't be logged.
  function onGuidedSkip(exIdx) {
    setSkippedIdx(prev => { const n = new Set(prev); n.add(exIdx); return n; });
  }
  function unskip(ri) {
    setSkippedIdx(prev => { const n = new Set(prev); n.delete(ri); return n; });
  }
  function resetGuided() { setMode('guided'); setGuidedStarted(false); setSkippedIdx(new Set()); }

  async function save() {
    setSaving(true); setError(''); setMsg('');
    const sets = [];
    let needBodyWeight = false;
    const bw = bodyWeight === '' ? null : Number(bodyWeight);
    rows.forEach((r, ri) => {
      if (skippedIdx.has(ri)) return; // coach skipped this exercise in guided mode
      const bodyweight = isBodyweight(r.name);
      r.sets.forEach((s, j) => {
        const w = (s.weight || '').toString().trim();
        const reps = (s.reps || '').toString().trim();
        if (w === '' && reps === '') return; // skip untouched sets
        let outWeight = w === '' ? '' : w;
        let body_weight = '', load_delta = '';
        if (bodyweight) {
          const delta = w === '' ? 0 : Number(w);
          load_delta = Number.isNaN(delta) ? 0 : delta;
          if (bw == null || Number.isNaN(bw)) { needBodyWeight = true; outWeight = ''; }
          else { body_weight = bw; outWeight = roundW(Math.max(0, bw + load_delta)); }
        }
        sets.push({
          exercise_id: r.exercise_id || '',
          exercise_name: r.name,
          exercise_code: r.code,
          set_number: j + 1,
          weight: outWeight,
          reps: reps === '' ? '' : reps,
          body_weight,
          load_delta,
        });
      });
    });
    if (sets.length === 0) { setError('Fill in at least one set.'); setSaving(false); return; }
    if (needBodyWeight) { setError("Add the client's current bodyweight above so their bodyweight sets (dips, chins…) count toward volume."); setSaving(false); return; }
    const payload = {
      programme_id: selProg && selProg.id ? selProg.id : '',
      phase_id: selPhase && selPhase.id ? selPhase.id : '',
      programme_name: selProg ? selProg.name : '',
      phase_name: selPhase ? selPhase.name : '',
      notes: notes.trim() || '',
      sets,
    };
    const { error: e } = await supabase.rpc('admin_log_training_session', { target_member: target.id, payload });
    setSaving(false);
    if (e) { setError(e.message || 'Could not save the session.'); return; }
    setMsg(`Session logged to ${target.name || target.email}. 💪`);
    clearDraft();
    setSelPhase(null); setSelProg(null); setRows([]); setNotes(''); resetGuided();
    supabase.rpc('admin_get_member_last_logs', { target_member: target.id }).then(({ data }) => setLastLogs(data || {}));
  }

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--grey-300)', background: '#fff', fontSize: '0.95rem' };
  const numStyle = { ...inputStyle, padding: '10px 8px', textAlign: 'center', fontSize: '1.05rem', fontWeight: 700 };

  // One exercise card with its sets — shared by the old-school list and the
  // guided one-at-a-time view.
  function renderExerciseCard(r, ri) {
    const last = lastFor(r.name);
    const bw = isBodyweight(r.name);
    const bwNum = bodyWeight === '' ? null : Number(bodyWeight);
    const skipped = skippedIdx.has(ri);
    return (
      <div key={ri} style={{ background: 'var(--grey-50)', borderRadius: 10, padding: 12, marginBottom: 10, opacity: skipped ? 0.5 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            {r.code && <span style={{ fontWeight: 800, color: 'var(--green)' }}>{r.code}</span>}
            <span style={{ fontWeight: 700 }}>{r.name}</span>
          </div>
          {skipped && <button type="button" onClick={() => unskip(ri)} style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--green)' }}>Skipped · undo</button>}
        </div>
        <div style={{ fontSize: '0.76rem', color: 'var(--grey-500)', marginBottom: 8 }}>
          {r.reps_target ? `Target ${r.reps_target} reps` : ''}{r.tempo ? ` · tempo ${r.tempo}` : ''}{r.rest ? ` · rest ${r.rest}s` : ''}
          {last ? <span style={{ color: 'var(--green)' }}>{(r.reps_target || r.tempo || r.rest) ? ' · ' : ''}last: {last}</span> : ''}
        </div>
        {!skipped && bw && (
          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--grey-500)', marginBottom: 2 }}>
            Weight box = + belt / − assist (blank = bodyweight only)
          </div>
        )}
        {!skipped && r.sets.map((s, si) => {
          const delta = (s.weight === '' || s.weight == null) ? 0 : Number(s.weight);
          const effective = (bw && bwNum != null && !Number.isNaN(bwNum)) ? roundW(Math.max(0, bwNum + delta)) : null;
          return (
            <div key={si} style={{ marginBottom: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '26px 1fr 1fr 26px', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--grey-500)', textAlign: 'center' }}>{si + 1}</span>
                <input style={numStyle} type="number" inputMode="decimal" value={s.weight}
                  onChange={e => setField(ri, si, 'weight', e.target.value)} placeholder={bw ? '± kg' : 'kg'} />
                <input style={numStyle} type="number" inputMode="numeric" value={s.reps}
                  onChange={e => setField(ri, si, 'reps', e.target.value)} placeholder="reps" />
                <button type="button" onClick={() => removeSet(ri, si)} disabled={r.sets.length <= 1}
                  style={{ color: r.sets.length <= 1 ? 'var(--grey-300)' : 'var(--danger)', fontSize: '1.1rem' }}>×</button>
              </div>
              {bw && effective != null && (
                <div style={{ fontSize: '0.68rem', color: 'var(--grey-500)', paddingLeft: 32, marginTop: 1 }}>
                  {delta === 0 ? 'Bodyweight only' : delta > 0 ? `Bodyweight + ${roundW(delta)}kg` : `Assisted ${roundW(-delta)}kg`} · counts as <strong style={{ color: 'var(--green)' }}>{effective}kg</strong>
                </div>
              )}
            </div>
          );
        })}
        {!skipped && <button type="button" onClick={() => addSet(ri)} style={{ color: 'var(--green)', fontWeight: 700, fontSize: '0.8rem' }}>+ Add set</button>}
      </div>
    );
  }

  const bwBanner = (
    <div style={{ background: (bodyWeight === '' ? '#FFF8F0' : 'var(--green-pale)'), border: `1px solid ${bodyWeight === '' ? '#FFD2B8' : 'var(--green-pale)'}`, borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', color: bodyWeight === '' ? '#C24E00' : 'var(--green)' }}>Client bodyweight</div>
        <input type="number" inputMode="decimal" value={bodyWeight} onChange={e => setBodyWeight(e.target.value)}
          placeholder="kg" style={{ width: 84, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--grey-300)', background: '#fff', fontSize: '0.95rem', textAlign: 'center', fontWeight: 700 }} />
        <span style={{ fontSize: '0.72rem', color: 'var(--grey-500)' }}>kg</span>
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)', marginTop: 6 }}>
        {scanWeight != null
          ? `From their latest scan (${scanWeight}kg) — edit if they've changed. Used so dips/chins count toward volume.`
          : 'No scan on file — type their current bodyweight so bodyweight sets count toward volume.'}
      </div>
    </div>
  );

  const segBtn = (active) => ({ flex: 1, padding: '8px 10px', borderRadius: 8, fontSize: '0.84rem', fontWeight: 700, cursor: 'pointer', border: 'none', background: active ? '#fff' : 'transparent', color: active ? 'var(--green)' : 'var(--grey-500)', boxShadow: active ? 'var(--shadow)' : 'none' });

  return (
    <div style={{ background: '#fff', borderRadius: 14, boxShadow: 'var(--shadow)', padding: 16 }}>
      <div style={{ fontWeight: 800, marginBottom: 10 }}>Run a live PT session</div>

      {/* Client picker */}
      {!target && (
        <>
          <input style={inputStyle} value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search client by name or email" />
          {search.trim() && (
            <div style={{ border: '1px solid var(--grey-200, #eee)', borderRadius: 8, marginTop: 6 }}>
              {filtered.length === 0 && <div style={{ padding: 10, color: 'var(--grey-500)', fontSize: '0.85rem' }}>No matches.</div>}
              {filtered.map(m => (
                <button key={m.id} type="button" onClick={() => pickMember(m)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', fontSize: '0.88rem', borderBottom: '1px solid var(--grey-100)' }}>
                  <span style={{ fontWeight: 700 }}>{m.name || '(no name)'}</span>
                  <span style={{ color: 'var(--grey-500)' }}> · {m.email}</span>
                  {m.is_prospect && (
                    <span style={{ marginLeft: 6, fontSize: '0.7rem', fontWeight: 700, color: '#FF6200', background: 'rgba(255,98,0,0.1)', padding: '1px 6px', borderRadius: 999 }}>No account yet</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}
      {target && (
        <div style={{ marginTop: 2, fontSize: '0.85rem', color: 'var(--green)' }}>
          Training <strong>{target.name || target.email}</strong> ·{' '}
          <button type="button" onClick={() => { clearDraft(); setTarget(null); setTree([]); setSelPhase(null); setSelProg(null); setRows([]); resetGuided(); }}
            style={{ color: 'var(--grey-500)', textDecoration: 'underline' }}>change client</button>
        </div>
      )}

      {/* Choose a workout */}
      {target && !selProg && (
        <div style={{ marginTop: 12 }}>
          {tree.length === 0 && <div style={{ color: 'var(--grey-500)', fontSize: '0.86rem' }}>This client has no programmes yet. Build one in the Training tab first.</div>}
          {tree.map(ph => (
            <div key={ph.id} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--grey-500)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>{ph.name}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(ph.programmes || []).map(pr => (
                  <button key={pr.id} type="button" onClick={() => openProgramme(ph, pr)}
                    style={{ fontSize: '0.86rem', fontWeight: 700, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--green)', color: 'var(--green)', background: '#fff', cursor: 'pointer' }}>
                    {pr.name || 'Workout'} <span style={{ color: 'var(--grey-500)', fontWeight: 500 }}>· {(pr.exercises || []).length} ex</span>
                  </button>
                ))}
                {(ph.programmes || []).length === 0 && <span style={{ color: 'var(--grey-500)', fontSize: '0.82rem' }}>No workouts.</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Live logging */}
      {target && selProg && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 800 }}>{selPhase && selPhase.name} · {selProg.name || 'Workout'}</div>
            <button type="button" onClick={() => { clearDraft(); setSelProg(null); setSelPhase(null); setRows([]); resetGuided(); }}
              style={{ color: 'var(--grey-500)', textDecoration: 'underline', fontSize: '0.8rem' }}>change workout</button>
          </div>

          {/* Guided (one at a time) vs old-school (whole list). Only one shows. */}
          <div style={{ display: 'flex', gap: 6, background: 'var(--grey-100)', borderRadius: 10, padding: 4, marginBottom: 12 }}>
            <button type="button" onClick={() => setMode('guided')} style={segBtn(mode === 'guided')}>▶ Guided</button>
            <button type="button" onClick={() => setMode('classic')} style={segBtn(mode === 'classic')}>≣ Old-school</button>
          </div>

          {/* Client bodyweight — shown on the old-school list and the guided plan
              overview. Once guided is running, the stepper shows its own banner
              per bodyweight exercise. */}
          {( mode === 'classic'
              ? rows.some(r => isBodyweight(r.name))
              : (!guidedStarted && rows.some(r => isBodyweight(r.name)))
           ) && bwBanner}

          {/* OLD-SCHOOL: the whole workout at once. */}
          {mode === 'classic' && (
            <>
              {rows.map((r, ri) => renderExerciseCard(r, ri))}
              <textarea style={{ ...inputStyle, minHeight: 64, marginTop: 4 }} value={notes}
                onChange={e => setNotes(e.target.value)} placeholder="Session notes (optional)" />
              {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: 10 }}>{error}</div>}
              <button type="button" className="btn btn-primary btn-full" style={{ marginTop: 12 }} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Finish & log session'}
              </button>
            </>
          )}

          {/* GUIDED — step 1: see the whole plan before starting. */}
          {mode === 'guided' && !guidedStarted && (
            <>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--grey-500)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
                The plan · {rows.length} exercise{rows.length === 1 ? '' : 's'}
              </div>
              {rows.map((r, ri) => (
                <div key={ri} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '8px 10px', background: 'var(--grey-50)', borderRadius: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--grey-400)', minWidth: 18 }}>{ri + 1}</span>
                  <div>
                    <div>
                      {r.code && <span style={{ fontWeight: 800, color: 'var(--green)', marginRight: 6 }}>{r.code}</span>}
                      <span style={{ fontWeight: 700 }}>{r.name}</span>
                    </div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--grey-500)', marginTop: 1 }}>
                      {r.sets.length} set{r.sets.length === 1 ? '' : 's'}{r.reps_target ? ` × ${r.reps_target}` : ''}{r.tempo ? ` · tempo ${r.tempo}` : ''}{r.rest ? ` · rest ${r.rest}s` : ''}
                      {lastFor(r.name) ? <span style={{ color: 'var(--green)' }}> · last: {lastFor(r.name)}</span> : ''}
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-primary btn-full" style={{ marginTop: 12 }}
                onClick={() => { setSkippedIdx(new Set()); setGuidedStarted(true); }} disabled={rows.length === 0}>
                ▶ Start guided session
              </button>
            </>
          )}

          {/* GUIDED — step 2: the member-style one-set-at-a-time stepper, with
              pre-filled reps/weight, +/- buttons, rest timer and swap/skip. */}
          {mode === 'guided' && guidedStarted && rows.length > 0 && (
            <AdminGuidedSession
              rows={rows} setField={setField} lastLogs={lastLogs}
              isBodyweight={isBodyweight} roundW={roundW}
              bodyWeight={bodyWeight} setBodyWeight={setBodyWeight} scanWeight={scanWeight}
              grouped={grouped} orderedParts={orderedParts}
              onSwap={onGuidedSwap} onSkip={onGuidedSkip}
              notes={notes} setNotes={setNotes}
              onFinish={save} saving={saving} error={error}
              onBackToPlan={() => setGuidedStarted(false)}
            />
          )}
        </div>
      )}

      {msg && <div style={{ color: 'var(--green)', fontSize: '0.88rem', marginTop: 12, fontWeight: 600 }}>{msg}</div>}
    </div>
  );
}

// ── Check-in: view a member's logged weights + body metrics/progress ────
function MemberCheckin() {
  const [members, setMembers] = useState([]);
  const [search, setSearch]   = useState('');
  const [target, setTarget]   = useState(null);
  const [history, setHistory] = useState([]);   // logged sessions
  const [scans, setScans]     = useState([]);   // inbody scans (newest first)
  const [loading, setLoading] = useState(false);

  // Feedback composer state (logged sessions)
  const [fbOpen, setFbOpen]     = useState(null);  // session id being commented on
  const [fbText, setFbText]     = useState('');
  const [fbSaving, setFbSaving] = useState(false);
  const [fbMsg, setFbMsg]       = useState('');

  // Feedback composer state (body scans)
  const [sfbOpen, setSfbOpen]     = useState(false);
  const [sfbText, setSfbText]     = useState('');
  const [sfbSaving, setSfbSaving] = useState(false);
  const [sfbMsg, setSfbMsg]       = useState('');

  useEffect(() => {
    supabase.rpc('admin_list_members').then(({ data }) => setMembers(data || []));
  }, []);

  function loadMember(memberId) {
    if (!memberId) { setHistory([]); setScans([]); return; }
    setLoading(true);
    Promise.all([
      supabase.rpc('admin_get_member_training_history', { target_member: memberId, limit_count: 100 }),
      supabase.rpc('admin_get_member_inbody_scans', { target_member: memberId }),
    ]).then(([h, s]) => {
      setHistory(h.data || []);
      setScans(s.data || []);
      setLoading(false);
    });
  }

  useEffect(() => {
    setFbOpen(null); setFbText(''); setFbMsg('');
    setSfbOpen(false); setSfbText(''); setSfbMsg('');
    loadMember(target ? target.id : null);
  }, [target]);

  function openFeedback(sess) {
    setFbMsg('');
    setFbText(sess.feedback && sess.feedback.note ? sess.feedback.note : '');
    setFbOpen(sess.id);
  }

  async function sendFeedback(sess) {
    const note = fbText.trim();
    if (!note || !target) return;
    setFbSaving(true); setFbMsg('');
    try {
      const { error } = await supabase.rpc('admin_set_session_feedback', { p_session_id: sess.id, p_note: note });
      if (error) { setFbMsg(`❌ ${error.message}`); setFbSaving(false); return; }
      // Fire a push via the existing admin endpoint (best effort).
      let pushNote = '';
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://tb-app-production.up.railway.app/api';
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${apiUrl}/admin/push`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({
            target: 'member', memberId: target.id,
            title: 'Your coach left you a note 💪',
            body: note.length > 120 ? note.slice(0, 117) + '…' : note,
          }),
        });
        const d = await res.json().catch(() => ({}));
        pushNote = (res.ok && d.ok && d.sent > 0)
          ? ' · notification sent'
          : ' · saved (member hasn\u2019t enabled notifications)';
      } catch (_) { pushNote = ' · saved'; }
      setFbMsg('✅ Feedback saved' + pushNote);
      setFbOpen(null); setFbText('');
      loadMember(target.id);
    } catch (err) {
      setFbMsg(`❌ ${err.message}`);
    }
    setFbSaving(false);
  }

  async function deleteFeedback(sess) {
    if (!target) return;
    const { error } = await supabase.rpc('admin_delete_session_feedback', { p_session_id: sess.id });
    if (!error) { setFbOpen(null); setFbText(''); setFbMsg(''); loadMember(target.id); }
  }

  // ── Body-scan feedback ───────────────────────────────────────────────
  function openScanFeedback(scan) {
    setSfbMsg('');
    setSfbText(scan && scan.feedback && scan.feedback.note ? scan.feedback.note : '');
    setSfbOpen(true);
  }

  async function sendScanFeedback(scan) {
    const note = sfbText.trim();
    if (!note || !target || !scan) return;
    setSfbSaving(true); setSfbMsg('');
    try {
      const { error } = await supabase.rpc('admin_set_scan_feedback', { p_scan_id: scan.id, p_note: note });
      if (error) { setSfbMsg(`❌ ${error.message}`); setSfbSaving(false); return; }
      let pushNote = '';
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://tb-app-production.up.railway.app/api';
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${apiUrl}/admin/push`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({
            target: 'member', memberId: target.id,
            title: 'Your coach reviewed your scan 📊',
            body: note.length > 120 ? note.slice(0, 117) + '…' : note,
          }),
        });
        const d = await res.json().catch(() => ({}));
        pushNote = (res.ok && d.ok && d.sent > 0)
          ? ' · notification sent'
          : ' · saved (member hasn\u2019t enabled notifications)';
      } catch (_) { pushNote = ' · saved'; }
      setSfbMsg('✅ Feedback saved' + pushNote);
      setSfbOpen(false); setSfbText('');
      loadMember(target.id);
    } catch (err) {
      setSfbMsg(`❌ ${err.message}`);
    }
    setSfbSaving(false);
  }

  async function deleteScanFeedback(scan) {
    if (!target || !scan) return;
    const { error } = await supabase.rpc('admin_delete_scan_feedback', { p_scan_id: scan.id });
    if (!error) { setSfbOpen(false); setSfbText(''); setSfbMsg(''); loadMember(target.id); }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members.slice(0, 8);
    return members.filter(m =>
      (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
    ).slice(0, 8);
  }, [members, search]);

  // Oldest-first scans for trend direction.
  const scansAsc = useMemo(() => [...scans].reverse(), [scans]);
  const latest = scans[0] || null;
  const trend = (key) => scansAsc.map(s => (s[key] == null ? null : Number(s[key])));

  // Personal bests per exercise from the logged sets.
  const bests = useMemo(() => {
    const map = new Map(); // name -> { heaviest, bestE1 }
    for (const sess of history) {
      for (const l of (sess.sets || [])) {
        const nm = l.exercise_name || '(unnamed)';
        const w = Number(l.weight), reps = Number(l.reps);
        if (!w || Number.isNaN(w)) continue;
        const e1 = tbE1rm(w, reps);
        const cur = map.get(nm) || { heaviest: null, bestE1: null };
        if (!cur.heaviest || w > cur.heaviest.w) cur.heaviest = { w, reps, on: sess.performed_on };
        if (e1 != null && (!cur.bestE1 || e1 > cur.bestE1.v)) cur.bestE1 = { v: e1, w, reps, on: sess.performed_on };
        map.set(nm, cur);
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [history]);

  // Estimated-1RM progression per exercise, oldest-first, for trend sparklines.
  const strengthTrends = useMemo(() => {
    const map = new Map(); // name -> [{ on, e1 }]  (session best e1RM)
    // history is newest-first; walk it and push, then reverse per exercise.
    for (const sess of history) {
      const perEx = new Map(); // name -> best e1 this session
      for (const l of (sess.sets || [])) {
        const w = Number(l.weight), reps = Number(l.reps);
        if (!w || Number.isNaN(w)) continue;
        const e1 = tbE1rm(w, reps);
        if (e1 == null) continue;
        const nm = l.exercise_name || '(unnamed)';
        if (!perEx.has(nm) || e1 > perEx.get(nm)) perEx.set(nm, e1);
      }
      for (const [nm, e1] of perEx.entries()) {
        const arr = map.get(nm) || [];
        arr.push({ on: sess.performed_on, e1 });
        map.set(nm, arr);
      }
    }
    return Array.from(map.entries())
      .map(([name, arr]) => [name, arr.slice().reverse()]) // oldest-first
      .filter(([, arr]) => arr.length >= 2)                // need 2+ points for a trend
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [history]);

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--grey-300)', background: '#fff', fontSize: '0.95rem' };
  const metricRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--grey-100)' };
  const fmt = (v, u) => (v == null || v === '' ? '—' : `${v}${u}`);

  return (
    <div style={{ background: '#fff', borderRadius: 14, boxShadow: 'var(--shadow)', padding: 16 }}>
      <div style={{ fontWeight: 800, marginBottom: 10 }}>Check in on a member</div>

      {/* Member picker */}
      <input style={inputStyle} value={target ? (target.name || target.email) : search}
        onChange={e => { setTarget(null); setSearch(e.target.value); }}
        placeholder="Search member by name or email" />
      {!target && search.trim() && (
        <div style={{ border: '1px solid var(--grey-200, #eee)', borderRadius: 8, marginTop: 6 }}>
          {filtered.length === 0 && <div style={{ padding: 10, color: 'var(--grey-500)', fontSize: '0.85rem' }}>No matches.</div>}
          {filtered.map(m => (
            <button key={m.id} type="button" onClick={() => { setTarget(m); setSearch(''); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', fontSize: '0.88rem', borderBottom: '1px solid var(--grey-100)' }}>
              <span style={{ fontWeight: 700 }}>{m.name || '(no name)'}</span>
              <span style={{ color: 'var(--grey-500)' }}> · {m.email}</span>
            </button>
          ))}
        </div>
      )}
      {target && (
        <div style={{ marginTop: 6, fontSize: '0.82rem', color: 'var(--green)' }}>
          Viewing <strong>{target.name || target.email}</strong> ·{' '}
          <button type="button" onClick={() => setTarget(null)} style={{ color: 'var(--grey-500)', textDecoration: 'underline' }}>change</button>
        </div>
      )}

      {target && loading && <div style={{ padding: 20, color: 'var(--grey-500)', fontSize: '0.85rem' }}>Loading…</div>}

      {target && !loading && (
        <>
          {/* Body metrics */}
          <div style={{ marginTop: 16, fontSize: '0.72rem', fontWeight: 700, color: 'var(--grey-500)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Body metrics (InBody)</div>
          {!latest ? (
            <div style={{ color: 'var(--grey-500)', fontSize: '0.85rem', padding: '8px 0' }}>No scans logged yet.</div>
          ) : (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: '0.74rem', color: 'var(--grey-500)', marginBottom: 6 }}>
                Latest: {latest.scanned_on} · {fmt(latest.weight_kg, 'kg')} · {fmt(latest.skeletal_muscle_kg, 'kg SMM')} · {fmt(latest.percent_body_fat, '% BF')}
              </div>
              {[
                { label: 'Muscle (SMM)', key: 'skeletal_muscle_kg', color: '#FF6200', unit: 'kg', lower: false },
                { label: 'Fat mass',     key: 'fat_mass_kg',        color: '#EF6C00', unit: 'kg', lower: true },
                { label: 'Body fat',     key: 'percent_body_fat',   color: '#2E7D32', unit: '%',  lower: true },
                { label: 'Weight',       key: 'weight_kg',          color: '#1565C0', unit: 'kg', lower: false },
                { label: 'InBody score', key: 'inbody_score',       color: '#6A1B9A', unit: '',   lower: false },
              ].map(m => (
                <div key={m.key} style={metricRow}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{m.label}</span>
                  <TbSparkline values={trend(m.key)} color={m.color} unit={m.unit} lowerIsBetter={m.lower} />
                </div>
              ))}

              {/* Coach feedback on the latest scan */}
              <div style={{ marginTop: 10 }}>
                {sfbMsg && <div style={{ fontSize: '0.8rem', marginBottom: 6, color: sfbMsg.startsWith('❌') ? '#C62828' : 'var(--green)' }}>{sfbMsg}</div>}
                {latest.feedback && !sfbOpen && (
                  <div style={{ padding: '8px 10px', background: 'var(--green-pale)', borderRadius: 8, fontSize: '0.8rem', color: 'var(--grey-700)', marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, color: 'var(--green)' }}>Coach: </span>{latest.feedback.note}
                    {latest.feedback.read_at
                      ? <span style={{ color: 'var(--grey-500)' }}> · read</span>
                      : <span style={{ color: 'var(--grey-500)' }}> · unread</span>}
                  </div>
                )}
                {!sfbOpen ? (
                  <button type="button" onClick={() => openScanFeedback(latest)}
                    style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--green)', cursor: 'pointer' }}>
                    {latest.feedback ? '✏️ Edit feedback on latest scan' : '💬 Leave feedback on latest scan'}
                  </button>
                ) : (
                  <div>
                    <textarea value={sfbText} onChange={e => setSfbText(e.target.value)} rows={3} maxLength={500}
                      placeholder="e.g. Muscle up, fat down — brilliant month. Keep protein high and we'll push again."
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--grey-300)', fontSize: '0.85rem', resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <button type="button" onClick={() => sendScanFeedback(latest)} disabled={sfbSaving || !sfbText.trim()}
                        style={{ flex: 1, padding: '9px 0', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', background: 'var(--green)', color: '#fff', opacity: (sfbSaving || !sfbText.trim()) ? 0.6 : 1, cursor: 'pointer' }}>
                        {sfbSaving ? 'Sending…' : 'Send to member'}
                      </button>
                      {latest.feedback && (
                        <button type="button" onClick={() => deleteScanFeedback(latest)} disabled={sfbSaving}
                          style={{ padding: '9px 12px', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', border: '1px solid var(--grey-300)', color: '#C62828', cursor: 'pointer' }}>
                          Delete
                        </button>
                      )}
                      <button type="button" onClick={() => { setSfbOpen(false); setSfbText(''); }} disabled={sfbSaving}
                        style={{ padding: '9px 12px', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', border: '1px solid var(--grey-300)', color: 'var(--grey-700)', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Personal bests */}
          <div style={{ marginTop: 18, fontSize: '0.72rem', fontWeight: 700, color: 'var(--grey-500)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Best lifts</div>
          {bests.length === 0 ? (
            <div style={{ color: 'var(--grey-500)', fontSize: '0.85rem', padding: '8px 0' }}>No logged sets yet.</div>
          ) : (
            <div style={{ marginTop: 6 }}>
              {bests.map(([name, b]) => (
                <div key={name} style={metricRow}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{name}</span>
                  <span style={{ fontSize: '0.8rem', textAlign: 'right' }}>
                    <span style={{ fontWeight: 800 }}>{b.heaviest ? `${b.heaviest.w}kg × ${b.heaviest.reps}` : '—'}</span>
                    {b.bestE1 && <span style={{ color: 'var(--grey-500)' }}> · est 1RM {tbRound1(b.bestE1.v)}kg</span>}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Strength trends (estimated 1RM over time) */}
          <div style={{ marginTop: 18, fontSize: '0.72rem', fontWeight: 700, color: 'var(--grey-500)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Strength trends (est. 1RM)</div>
          {strengthTrends.length === 0 ? (
            <div style={{ color: 'var(--grey-500)', fontSize: '0.85rem', padding: '8px 0' }}>Need at least two logged sessions per lift to show a trend.</div>
          ) : (
            <div style={{ marginTop: 6 }}>
              {strengthTrends.map(([name, arr]) => (
                <div key={name} style={metricRow}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{name}</span>
                  <TbSparkline values={arr.map(p => tbRound1(p.e1))} color="#C62828" unit="kg" lowerIsBetter={false} />
                </div>
              ))}
            </div>
          )}

          {/* Recent sessions */}
          <div style={{ marginTop: 18, fontSize: '0.72rem', fontWeight: 700, color: 'var(--grey-500)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Recent sessions</div>
          {history.length === 0 ? (
            <div style={{ color: 'var(--grey-500)', fontSize: '0.85rem', padding: '8px 0' }}>No sessions logged yet.</div>
          ) : (
            <div style={{ marginTop: 6 }}>
              {fbMsg && <div style={{ fontSize: '0.8rem', marginBottom: 8, color: fbMsg.startsWith('❌') ? '#C62828' : 'var(--green)' }}>{fbMsg}</div>}
              {history.slice(0, 12).map(sess => (
                <div key={sess.id} style={{ background: 'var(--grey-50)', borderRadius: 10, padding: 10, marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>
                      {sess.performed_on} <span style={{ color: 'var(--grey-500)', fontWeight: 500 }}>· {sess.phase_name || ''} {sess.programme_name || ''}</span>
                    </div>
                    <button type="button" onClick={() => (fbOpen === sess.id ? setFbOpen(null) : openFeedback(sess))}
                      style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--green)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                      {sess.feedback ? '✏️ Edit feedback' : '💬 Leave feedback'}
                    </button>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    {(sess.sets || []).map(l => {
                      const isMobSet = l.hold_seconds != null && l.weight == null && l.reps == null;
                      const cBits = [];
                      if (l.duration_min != null) cBits.push(`${l.duration_min} min`);
                      if (l.distance != null) cBits.push(`${l.distance} dist`);
                      if (l.resistance != null) cBits.push(`res ${l.resistance}`);
                      if (l.calories != null) cBits.push(`${l.calories} cal`);
                      const isCardioSet = !isMobSet && cBits.length > 0 && l.weight == null && l.reps == null;
                      const val = isMobSet
                        ? (Number(l.hold_seconds) > 0 ? `⏱ ${l.hold_seconds}s` : '✓ done')
                        : isCardioSet
                          ? cBits.join(' · ')
                          : `${l.weight != null ? `${l.weight}kg` : 'BW'} × ${l.reps != null ? l.reps : '—'}`;
                      return (
                      <div key={l.id} style={{ fontSize: '0.78rem', color: 'var(--grey-700)', display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                        <span>{l.exercise_code ? `${l.exercise_code} ` : ''}{l.exercise_name}</span>
                        <span style={{ fontWeight: 600, color: isMobSet ? '#C24E00' : undefined }}>{val}</span>
                      </div>
                      );
                    })}
                    {(sess.sets || []).length === 0 && <div style={{ fontSize: '0.76rem', color: 'var(--grey-500)' }}>No sets recorded.</div>}
                  </div>

                  {/* Existing saved note */}
                  {sess.feedback && fbOpen !== sess.id && (
                    <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--green-pale)', borderRadius: 8, fontSize: '0.8rem', color: 'var(--grey-700)' }}>
                      <span style={{ fontWeight: 700, color: 'var(--green)' }}>Coach: </span>{sess.feedback.note}
                      {sess.feedback.read_at
                        ? <span style={{ color: 'var(--grey-500)' }}> · read</span>
                        : <span style={{ color: 'var(--grey-500)' }}> · unread</span>}
                    </div>
                  )}

                  {/* Composer */}
                  {fbOpen === sess.id && (
                    <div style={{ marginTop: 8 }}>
                      <textarea value={fbText} onChange={e => setFbText(e.target.value)} rows={3} maxLength={500}
                        placeholder="e.g. Great depth on squats — let's add 2.5kg next week."
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--grey-300)', fontSize: '0.85rem', resize: 'vertical' }} />
                      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <button type="button" onClick={() => sendFeedback(sess)} disabled={fbSaving || !fbText.trim()}
                          style={{ flex: 1, padding: '9px 0', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', background: 'var(--green)', color: '#fff', opacity: (fbSaving || !fbText.trim()) ? 0.6 : 1, cursor: 'pointer' }}>
                          {fbSaving ? 'Sending…' : 'Send to member'}
                        </button>
                        {sess.feedback && (
                          <button type="button" onClick={() => deleteFeedback(sess)} disabled={fbSaving}
                            style={{ padding: '9px 12px', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', border: '1px solid var(--grey-300)', color: '#C62828', cursor: 'pointer' }}>
                            Delete
                          </button>
                        )}
                        <button type="button" onClick={() => { setFbOpen(null); setFbText(''); }} disabled={fbSaving}
                          style={{ padding: '9px 12px', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', border: '1px solid var(--grey-300)', color: 'var(--grey-700)', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [selectedMember, setSelectedMember] = useState(null);
  const [tab, setTab] = useState('members');

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span style={{ fontSize: '1.4rem' }}>⚙️</span>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 800 }}>Admin</h1>
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 20 }}>
        {[
          { key: 'members',      label: '👤 Members'      },
          { key: 'bookings',     label: '📅 Today'         },
          { key: 'transactions', label: '💳 Transactions'  },
          { key: 'slots',        label: '🗓 Slots'          },
          { key: 'notify',       label: '🔔 Notify'        },
          { key: 'payments',     label: '⚠️ Payments'      },
          { key: 'training',     label: '📝 Write Programme' },
          { key: 'live',         label: '🏋️ PT Live'      },
          { key: 'checkin',      label: '📈 Check-in'      },
        ].map(t => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} style={{
            padding: '10px 6px', borderRadius: 10, fontWeight: 700, fontSize: '0.82rem',
            border: `2px solid ${tab === t.key ? 'var(--green)' : 'var(--grey-300)'}`,
            background: tab === t.key ? 'var(--green-pale)' : '#fff',
            color: tab === t.key ? 'var(--green)' : 'var(--grey-700)', cursor: 'pointer',
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'members' && (
        <>
          <div className="section-title">Find a Member</div>
          <MemberSearch onSelect={m => setSelectedMember(m)} />
          {selectedMember && <MemberPanel member={selectedMember} onClose={() => setSelectedMember(null)} />}
        </>
      )}

      {tab === 'bookings' && <TodaysBookings />}
      {tab === 'transactions' && <GlobalTransactions />}
      {tab === 'slots' && (
        <>
          <div className="section-title">Manage Availability</div>
          <BiohackDayManager />
          <SlotManager />
        </>
      )}
      {tab === 'notify' && (
        <>
          <div className="section-title">Send a Notification</div>
          <Notifications />
        </>
      )}
      {tab === 'payments' && <OrphanedPayments />}
      {tab === 'training' && (
        <>
          <div className="section-title">Write a Programme</div>
          <TrainingAssign />
        </>
      )}
      {tab === 'live' && (
        <>
          <div className="section-title">Run a Live PT Session</div>
          <AdminLiveSession />
        </>
      )}
      {tab === 'checkin' && (
        <>
          <div className="section-title">Member Check-in</div>
          <MemberCheckin />
        </>
      )}
    </div>
  );
}
